import { currentYear } from "@/lib/siteYear";
// SEO landing config for /pressure-washers/category/:slug.
// Same pattern as airCompressorSubcategoryConfig — article + Amazon CTA + sibling chips.

export type PressureWasherGroup =
  | "type"
  | "use"
  | "pressure"
  | "power"
  | "brand"
  | "user"
  | "noise";

export interface PressureWasherSubcategoryConfig {
  slug: string;
  title: string;
  shortLabel: string;
  group: PressureWasherGroup;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  longCopy: string[];
  faqs: { q: string; a: string }[];
  amazonQuery: string;
  tagKeywords: string[];
}

const Y = currentYear();

type Seed = Omit<PressureWasherSubcategoryConfig, "tagKeywords"> & {
  tagKeywords?: string[];
};

const seeds: Seed[] = [
  // ============ BY TYPE ============
  {
    slug: "electric-pressure-washers",
    title: "Electric Pressure Washers",
    shortLabel: "Electric",
    group: "type",
    h1: `Best Electric Pressure Washers UK ${Y}`,
    metaTitle: `Best Electric Pressure Washers UK ${Y} | Mains-Powered Reviews`,
    metaDescription:
      "Independent UK reviews of the best electric pressure washers. Compare mains-powered Kärcher, Nilfisk, Bosch and Hyundai models for patios, cars and driveways.",
    intro:
      "Electric pressure washers are the most popular choice for UK households — quieter than petrol, maintenance-light, and powerful enough for everything from cars to driveways.",
    longCopy: [
      "A modern 1800–2500W electric pressure washer delivers 110–160 bar — easily enough to lift moss from block paving, strip dirt from decking and clean cars without scratching the paint. Most run from a standard 13A plug.",
      "Look for induction motors (longer life than universal motors), auto-stop triggers, and a hose reel. The Kärcher K4/K5, Nilfisk Core and Bosch UniversalAquatak ranges are the consistent best-sellers in the UK market.",
      "Trade-offs: a 10m mains cable and water-tap connection limit reach, so for remote yards or large estates a petrol or battery model may suit better.",
    ],
    faqs: [
      { q: "How powerful is a good electric pressure washer?", a: "For mixed home use, 130–150 bar at 6–8 L/min covers cars, patios and driveways. Below 110 bar struggles with stubborn moss; above 160 bar is overkill for most domestic surfaces." },
      { q: "Are electric pressure washers reliable?", a: "Quality models with induction motors and brass pump heads (e.g. Kärcher K5, Nilfisk Core 140) routinely last 8–10 years of weekend use. Cheap universal-motor units typically last 2–4 seasons." },
      { q: "Can I run an electric pressure washer from a water butt?", a: "Only if the model supports suction (check for a self-priming spec). Most domestic electric washers need pressurised tap supply at 8+ L/min." },
    ],
    amazonQuery: "electric pressure washer",
  },
  {
    slug: "cordless-pressure-washers",
    title: "Cordless Pressure Washers",
    shortLabel: "Cordless",
    group: "type",
    h1: `Best Cordless Pressure Washers UK ${Y}`,
    metaTitle: `Best Cordless Pressure Washers UK ${Y} | Battery-Powered Reviews`,
    metaDescription:
      "The best cordless pressure washers in the UK reviewed. Battery models from Bosch, Makita, DeWalt, Worx and Ryobi compared for cars, bikes and quick clean-ups.",
    intro:
      "Cordless pressure washers free you from sockets and taps — perfect for cleaning cars away from home, muddy bikes, caravans and allotments. Power has improved sharply in 2025–2026.",
    longCopy: [
      "Most cordless washers run on 18V or 36V (dual-18V) platforms, drawing water from a bucket, water butt or jerry can. Expect 20–40 bar at 2–4 L/min — a fraction of a mains washer, but plenty for light cleaning.",
      "Bosch Fontus, Worx Hydroshot, Karcher KHB cordless, Makita DHW080 and DeWalt DCPW550 are the standout UK models. Battery life is typically 20–30 minutes on full trigger.",
      "Cordless models suit detailing, bike washing, dog grooming and quick patio rinses. For weekly driveway cleans, a mains electric model is still the better tool.",
    ],
    faqs: [
      { q: "Are cordless pressure washers any good?", a: "For light-duty cleaning — yes, especially the 36V/dual-battery models. Don't expect mains-level pressure for stripping moss from patios." },
      { q: "How long does a battery pressure washer last per charge?", a: "Typical runtime is 20–35 minutes on a 4–5Ah pack. Carry a spare battery for full-vehicle washes." },
      { q: "Can I refill from a bucket?", a: "Yes — every quality cordless washer ships with a suction hose for bucket, water butt or pond use." },
    ],
    amazonQuery: "cordless pressure washer",
  },
  {
    slug: "battery-powered-pressure-washers",
    title: "Battery Powered Pressure Washers",
    shortLabel: "Battery",
    group: "type",
    h1: `Best Battery Powered Pressure Washers UK ${Y}`,
    metaTitle: `Best Battery Powered Pressure Washers UK ${Y} | Reviews & Picks`,
    metaDescription:
      "Compare the best battery powered pressure washers in the UK. 18V and 36V models from Makita, DeWalt, Bosch, Karcher and Worx tested and rated.",
    intro:
      "Battery powered pressure washers are the fastest-growing segment of the UK market. Brushless motors and 36V dual-battery platforms now deliver real cleaning power away from mains.",
    longCopy: [
      "The latest battery washers (Karcher KHB 6 Cordless, Bosch Fontus 18V-30, Makita DHW080) deliver 24–40 bar and run on existing tool platforms — so if you already own Makita LXT, DeWalt XR or Bosch 18V batteries, you save on the kit price.",
      "Match the battery voltage to expected pressure: 18V single battery for inflation and rinsing, 36V/dual-18V for serious cleaning. A 5Ah pack typically delivers 25–35 minutes of trigger time.",
      "Best for car detailers, cyclists, dog owners and anyone needing a portable washer at the allotment, beach or campsite.",
    ],
    faqs: [
      { q: "18V or 36V battery pressure washer?", a: "18V suits inflation and light rinsing. 36V (or dual-18V) delivers double the pressure and is the better all-round choice." },
      { q: "Can a battery pressure washer clean a patio?", a: "Yes for small areas with a surface-cleaner attachment, but a mains washer is faster for anything over 20 m²." },
      { q: "Do battery pressure washers need a water supply?", a: "Not a tap — they draw from a bucket, water butt, jerry can or pond via a suction hose included in the box." },
    ],
    amazonQuery: "battery pressure washer",
  },
  {
    slug: "petrol-pressure-washers",
    title: "Petrol Pressure Washers",
    shortLabel: "Petrol",
    group: "type",
    h1: `Best Petrol Pressure Washers UK ${Y}`,
    metaTitle: `Best Petrol Pressure Washers UK ${Y} | Heavy-Duty Reviews`,
    metaDescription:
      "Independent UK reviews of petrol pressure washers. Compare Hyundai, Honda, P1PE and SwitZer models from 200 to 300+ bar for trade, agricultural and off-grid use.",
    intro:
      "Petrol pressure washers deliver mains-beating pressure with no cable to trip over — the right choice for farms, building sites and any property without convenient power.",
    longCopy: [
      "Domestic petrol models start around 200 bar at 9 L/min; trade and agricultural units reach 250–300 bar with Honda GX-series engines. Expect 6.5–13 HP four-strokes with axial or triplex pumps.",
      "UK favourites include the Hyundai HYW3100P, P1PE P3200PWT, SwitZer SZSW17 and Honda-powered Mac Allister units. Triplex pump models cost more but last 3–5× longer than axial-pump equivalents.",
      "Best for builders, farmers, smallholders, contractors and anyone with significant patio or driveway acreage. Avoid using indoors due to exhaust fumes.",
    ],
    faqs: [
      { q: "How powerful is a petrol pressure washer?", a: "Domestic petrol washers run 180–220 bar; commercial models reach 250–300+ bar. That's roughly double the pressure of a top mains electric." },
      { q: "Honda vs Hyundai petrol pressure washer engine?", a: "Honda GX-series is the gold standard for reliability. Hyundai's own engines are improving fast and cost noticeably less for comparable specs." },
      { q: "Do I need a special pump for a petrol pressure washer?", a: "For occasional use, axial cam pumps are fine. For daily trade or agricultural use, triplex plunger pumps last far longer and are worth the extra spend." },
    ],
    amazonQuery: "petrol pressure washer",
  },
  {
    slug: "diesel-pressure-washers",
    title: "Diesel Pressure Washers",
    shortLabel: "Diesel",
    group: "type",
    h1: `Best Diesel Pressure Washers UK ${Y}`,
    metaTitle: `Best Diesel Pressure Washers UK ${Y} | Industrial & Hot Wash Reviews`,
    metaDescription:
      "UK guide to diesel pressure washers — cold and hot-water industrial models from Kärcher, Nilfisk, Lavor and SIP. Compare 200–500 bar contractor and farm units.",
    intro:
      "Diesel pressure washers are the workhorses of farms, fleet yards and industrial wash bays. Long runtime, high torque and compatibility with hot-water burner heads make them the contractor's choice.",
    longCopy: [
      "Diesel washers split into two categories: cold-water diesel engine units (200–250 bar, mobile) and hot-water diesel-burner units (electric motor + diesel heater) for grease, oil and abattoir-grade cleaning.",
      "Top UK options include Kärcher HDS hot-water trailers, Nilfisk MH 7P, Lavor Thermic 17H and SIP Tempest. Expect £2,000–£12,000+ depending on size, hot/cold and trailer-mount.",
      "Choose diesel if you wash multiple machines a day, need hot-water grease cutting, or work off-grid for long shifts.",
    ],
    faqs: [
      { q: "Diesel vs petrol pressure washer?", a: "Diesel offers longer runtime, better fuel economy under load, and pairs with hot-water burners. Petrol is lighter, cheaper and starts more easily in cold weather." },
      { q: "Are diesel pressure washers hot water?", a: "Some are — they use a diesel-fired burner to heat the water. Others use a diesel engine to drive a cold-water pump. Always check the spec." },
      { q: "What pressure does a diesel pressure washer produce?", a: "Industrial diesel-engine units run 200–350 bar; trailer-mounted hot-water units can reach 500 bar for heavy degreasing." },
    ],
    amazonQuery: "diesel pressure washer",
  },
  {
    slug: "hot-water-pressure-washers",
    title: "Hot Water Pressure Washers",
    shortLabel: "Hot Water",
    group: "type",
    h1: `Best Hot Water Pressure Washers UK ${Y}`,
    metaTitle: `Best Hot Water Pressure Washers UK ${Y} | Grease & Oil Cleaning`,
    metaDescription:
      "Independent UK guide to hot water pressure washers. Diesel-burner Kärcher HDS, Nilfisk MH and Lavor models for grease, oil and food-industry cleaning.",
    intro:
      "Hot water pressure washers cut through grease, oil and stuck-on grime in a fraction of the time of cold-water units. They're the standard tool for kitchens, food processing, fleet yards and engine bays.",
    longCopy: [
      "A hot-water washer pairs a high-pressure pump with a diesel-fired coil that heats water to 80–155°C. The combination of pressure plus heat dissolves oily films that cold water simply pushes around.",
      "Kärcher HDS, Nilfisk MH, Lavor Thermic and Karcher HDS Trailer ranges dominate UK trade sales. Expect £2,500–£15,000+. Most need 3-phase power; some are diesel-engine driven for full mobility.",
      "Best for food-industry cleaning, vehicle workshops, abattoirs, oil/gas yards and any application where grease is the enemy.",
    ],
    faqs: [
      { q: "Why use a hot water pressure washer?", a: "Heat dissolves grease, oil and protein-based dirt that cold water can't shift. It also sanitises surfaces — important for food-handling environments." },
      { q: "How hot does a hot water pressure washer get?", a: "Domestic units reach 60–80°C; industrial hot-water washers produce steam at 140–155°C." },
      { q: "Are hot water pressure washers worth the price?", a: "For trade users cleaning oily machinery or food equipment daily — yes, the time saved pays back fast. For domestic use, cold water plus detergent is usually fine." },
    ],
    amazonQuery: "hot water pressure washer",
  },
  {
    slug: "cold-water-pressure-washers",
    title: "Cold Water Pressure Washers",
    shortLabel: "Cold Water",
    group: "type",
    h1: `Best Cold Water Pressure Washers UK ${Y}`,
    metaTitle: `Best Cold Water Pressure Washers UK ${Y} | Domestic & Trade Reviews`,
    metaDescription:
      "The best cold water pressure washers in the UK reviewed. Compare domestic Kärcher and Nilfisk models to professional cold-water industrial units.",
    intro:
      "Cold water pressure washers cover 95% of UK cleaning jobs — patios, driveways, cars, decking, fencing and brickwork. They're cheaper, simpler and need no diesel for the burner.",
    longCopy: [
      "Cold-water washers range from £80 entry-level Kärcher K2 units to £4,000+ trade machines from Nilfisk MC and Comet KS. Pressure runs from 100 bar (domestic) to 250+ bar (professional).",
      "Key things to check: pump material (aluminium for occasional, brass for daily use), motor type (induction is best), and total-stop function. A 6 L/min flow rate is the practical minimum for patio cleaning.",
      "Choose cold-water unless you specifically need to dissolve grease or sanitise food surfaces — then a hot-water diesel-burner unit is worth the extra cost.",
    ],
    faqs: [
      { q: "Is cold water enough for cleaning a patio?", a: "Yes — 130+ bar cold water plus a surface-cleaner attachment strips moss and dirt from block paving in minutes." },
      { q: "Cold water vs hot water pressure washer?", a: "Cold is cheaper, lighter and fine for general dirt. Hot wins for grease, oil and protein-based contamination." },
      { q: "Can I add detergent to a cold pressure washer?", a: "Yes — most modern cold-water washers have a built-in detergent tank or external pickup hose. Use only pressure-washer-rated soaps to protect the pump seals." },
    ],
    amazonQuery: "cold water pressure washer",
  },
  {
    slug: "compact-pressure-washers",
    title: "Compact Pressure Washers",
    shortLabel: "Compact",
    group: "type",
    h1: `Best Compact Pressure Washers UK ${Y}`,
    metaTitle: `Best Compact Pressure Washers UK ${Y} | Space-Saving Reviews`,
    metaDescription:
      "The best compact pressure washers in the UK — space-saving Kärcher, Nilfisk and Bosch models perfect for small gardens, flats and easy storage.",
    intro:
      "Compact pressure washers pack mains-grade cleaning into a footprint that fits a kitchen cupboard. Ideal for small gardens, flats with a balcony, and anyone short on shed space.",
    longCopy: [
      "Modern compact washers (Kärcher K2 Compact, Nilfisk Core 125, Bosch UniversalAquatak 125) weigh 5–7 kg and stand under 30 cm tall. Expect 110–125 bar at 5–6 L/min — plenty for cars, garden furniture and small patios.",
      "Look for integrated hose storage, a quick-connect tap fitting and a carry handle. Most compact units now ship with a patio-cleaner attachment as standard.",
      "Trade-off: smaller pumps mean lower duty cycles. For sustained driveway cleaning, step up to a mid-size 140–150 bar model.",
    ],
    faqs: [
      { q: "Are compact pressure washers powerful enough?", a: "For cars, bikes and small patios, yes. For full driveways or large brickwork projects, a mid-size 140+ bar model is faster." },
      { q: "How do you store a compact pressure washer?", a: "Drain the pump, coil the hose, and store indoors during winter to prevent frost damage. Most compact models fit on a garage shelf." },
      { q: "Best compact pressure washer brand UK?", a: "Kärcher dominates with the K2 Compact range. Nilfisk Core and Bosch EasyAquatak are strong alternatives." },
    ],
    amazonQuery: "compact pressure washer",
  },
  {
    slug: "portable-pressure-washers",
    title: "Portable Pressure Washers",
    shortLabel: "Portable",
    group: "type",
    h1: `Best Portable Pressure Washers UK ${Y}`,
    metaTitle: `Best Portable Pressure Washers UK ${Y} | Mobile & Wheeled Reviews`,
    metaDescription:
      "The best portable pressure washers in the UK reviewed. Wheeled and cordless models for cleaning on the move — cars, caravans, bikes and allotments.",
    intro:
      "Portable pressure washers mean you can clean wherever the dirt is — driveways, caravans, allotments, horse boxes — without dragging extension cables across the lawn.",
    longCopy: [
      "Two flavours: mains-powered wheeled units (10m cable, 8m hose, trolley design) for around-the-property work, and battery washers that draw from a bucket for true off-grid cleaning.",
      "Wheeled standouts include the Kärcher K5 Premium Smart Control, Nilfisk Core 140 Car Wash and Bosch AdvancedAquatak 160. For cordless portability, Bosch Fontus 18V-30 and Worx Hydroshot lead the field.",
      "Match wheels and weight to your terrain — pneumatic tyres roll over gravel better than plastic castors, which struggle on uneven ground.",
    ],
    faqs: [
      { q: "Mains or battery portable pressure washer?", a: "Mains wheeled units give full cleaning power around a property. Battery models extend reach to anywhere with a bucket of water." },
      { q: "How heavy is a portable pressure washer?", a: "Compact mains models 5–8 kg; mid-size wheeled units 10–15 kg; petrol portables 25–35 kg." },
      { q: "Best portable pressure washer for caravans?", a: "Battery-powered Bosch Fontus or Kärcher OC 3 — both draw from a bucket, perfect for campsite use." },
    ],
    amazonQuery: "portable pressure washer",
  },
  {
    slug: "handheld-pressure-washers",
    title: "Handheld Pressure Washers",
    shortLabel: "Handheld",
    group: "type",
    h1: `Best Handheld Pressure Washers UK ${Y}`,
    metaTitle: `Best Handheld Pressure Washers UK ${Y} | Cordless Picks`,
    metaDescription:
      "Independent UK reviews of handheld pressure washers. Compact battery cleaners from Worx, Bosch, Karcher and Ryobi for cars, bikes and quick clean-ups.",
    intro:
      "Handheld pressure washers are pistol-grip cordless units that draw water from a bucket — light, fast and perfect for quick jobs where a full trolley is overkill.",
    longCopy: [
      "All-in-one designs like the Worx Hydroshot, Karcher KHB 5 and Bosch Fontus combine motor, pump and battery in a single grip-and-go unit. Expect 20–30 bar and 30 minutes runtime per charge.",
      "Best for muddy bikes, dog washing, beach gear, car detailing and rinsing tools after a job. Most accept water from a bucket, water butt, jerry can or even a still lake.",
      "Don't expect these to clean a patio in a hurry — they're light-duty rinsers, not driveway cleaners.",
    ],
    faqs: [
      { q: "Are handheld pressure washers any good?", a: "For light cleaning and rinsing — yes. For sustained patio or driveway work — no, you need a mains or petrol washer." },
      { q: "Can I use a handheld pressure washer to wash my car?", a: "Yes — most owners use them for foam-then-rinse car washing. Power is gentler on paintwork than a mains washer." },
      { q: "How much water does a handheld pressure washer use?", a: "Roughly 2–4 L/min — a typical bucket lasts 3–5 minutes of trigger time." },
    ],
    amazonQuery: "handheld pressure washer",
  },
  {
    slug: "commercial-pressure-washers",
    title: "Commercial Pressure Washers",
    shortLabel: "Commercial",
    group: "type",
    h1: `Best Commercial Pressure Washers UK ${Y}`,
    metaTitle: `Best Commercial Pressure Washers UK ${Y} | Trade Reviews`,
    metaDescription:
      "Trade-grade pressure washers reviewed for UK commercial use. Compare Kärcher HD, Nilfisk MC and Comet cold and hot-water units for daily contract work.",
    intro:
      "Commercial pressure washers are built for daily 8-hour use with brass pump heads, ceramic pistons and induction motors. Heavier, costlier and far longer-lived than DIY units.",
    longCopy: [
      "Hallmarks of a commercial machine: triplex plunger pump, brass cylinder head, ceramic pistons, 1450 RPM induction motor, full thermal protection and replaceable seals. Expect 150–250 bar.",
      "Top UK contractor picks: Kärcher HD 5/15, Nilfisk MC 2C, Comet KS 1900 Classic. Hot-water Kärcher HDS and Nilfisk MH equivalents add diesel burners for grease work.",
      "Pricing runs £700–£4,000+ for cold-water, £2,500–£12,000+ for hot. Total cost of ownership is far lower than burning through cheap domestic units annually.",
    ],
    faqs: [
      { q: "What makes a pressure washer commercial?", a: "Daily-use construction — triplex pump, brass head, ceramic pistons, induction motor, serviceable seals and a 3-year+ warranty." },
      { q: "Do I need 3-phase power for a commercial pressure washer?", a: "Single-phase 240V covers up to about 200 bar / 4 kW. Beyond that, 3-phase 415V is standard." },
      { q: "How long do commercial pressure washers last?", a: "10,000–20,000 hours with seal kits and oil changes — typically 8–15 years of daily trade use." },
    ],
    amazonQuery: "commercial pressure washer",
  },
  {
    slug: "industrial-pressure-washers",
    title: "Industrial Pressure Washers",
    shortLabel: "Industrial",
    group: "type",
    h1: `Best Industrial Pressure Washers UK ${Y}`,
    metaTitle: `Best Industrial Pressure Washers UK ${Y} | Heavy-Duty Picks`,
    metaDescription:
      "UK guide to industrial pressure washers. Stationary and trailer-mounted Kärcher, Nilfisk, Falch and Comet units for factories, plant hire and contractors.",
    intro:
      "Industrial pressure washers serve factories, plant hire yards and waste sites — three-phase, hot-water capable, and built for continuous duty across multiple operators.",
    longCopy: [
      "Industrial-grade units run 200–500 bar with flow rates of 15–60 L/min, drawing 5–25 kW from 3-phase supplies. Most are stationary wall-mount or trailer-mounted with onboard water tanks.",
      "Falch, Hammelmann, Kärcher HDS, Nilfisk Alto Neptune and Comet KS deliver the bulk of the UK industrial fleet. Trailer hot-water units are essential for graffiti removal and surface preparation contractors.",
      "Expect £4,000–£40,000+ depending on configuration. Most industrial users buy via specialist distributors with full service contracts.",
    ],
    faqs: [
      { q: "Industrial vs commercial pressure washer?", a: "Commercial = trade-grade single-phase. Industrial = 3-phase, higher pressure, often hot-water, and built for multi-shift use." },
      { q: "What pressure is industrial?", a: "Typically 200–500 bar. Surface-prep and concrete-removal units go higher — 1000–2800 bar in ultra-high-pressure water-jetting." },
      { q: "Are trailer pressure washers worth it?", a: "For mobile contractors — yes. Onboard tank plus diesel burner means working anywhere without site water." },
    ],
    amazonQuery: "industrial pressure washer",
  },
  {
    slug: "professional-pressure-washers",
    title: "Professional Pressure Washers",
    shortLabel: "Professional",
    group: "type",
    h1: `Best Professional Pressure Washers UK ${Y}`,
    metaTitle: `Best Professional Pressure Washers UK ${Y} | Trade Reviews`,
    metaDescription:
      "The best professional pressure washers in the UK reviewed. Kärcher Professional, Nilfisk MC, Comet and SIP cold and hot models for full-time trade.",
    intro:
      "Professional pressure washers sit between domestic and full industrial — daily trade use, single-phase compatible, with the pump quality to handle 8 hours a day without complaint.",
    longCopy: [
      "Professional models share the triplex pumps and brass heads of industrial units but stay single-phase and around 150–200 bar. Ideal for window cleaners, valeters, soft-wash crews and gardeners.",
      "Top sellers: Kärcher HD 5/15, Nilfisk MC 2C, Comet KS 1900, SIP Tempest TP750. Expect £700–£2,500 cold-water; £2,500–£6,000 hot-water.",
      "Worth the spend if you wash for a living. For weekend domestic use, a £200–£400 prosumer like the Kärcher K7 saves money without compromise.",
    ],
    faqs: [
      { q: "Domestic vs professional pressure washer?", a: "Pro models use triplex pumps, brass heads and induction motors — lasting 5–10× longer under daily use. Domestic models are fine for weekly weekend work." },
      { q: "How long do professional pressure washers last?", a: "8,000–15,000 hours with regular seal kits — easily 8–12 years of daily trade." },
      { q: "Best pro pressure washer for window cleaning?", a: "Lower-pressure (100–120 bar) high-flow models like the Kärcher HD 6/15 C or Nilfisk MC 4M, paired with a window-cleaning brush." },
    ],
    amazonQuery: "professional pressure washer",
  },
  {
    slug: "heavy-duty-pressure-washers",
    title: "Heavy Duty Pressure Washers",
    shortLabel: "Heavy Duty",
    group: "type",
    h1: `Best Heavy Duty Pressure Washers UK ${Y}`,
    metaTitle: `Best Heavy Duty Pressure Washers UK ${Y} | Top Reviews`,
    metaDescription:
      "The toughest heavy duty pressure washers in the UK. Petrol, diesel and 3-phase models for farms, building sites and contractors compared and rated.",
    intro:
      "Heavy duty pressure washers shrug off mud, oil, concrete dust and frost — built for farms, building sites, mechanics and anyone whose washer takes daily abuse.",
    longCopy: [
      "Hallmarks: petrol or 3-phase electric motors, triplex plunger pumps, steel frames with full roll bars, and 250+ bar pressure. Tank-mounted accessories like surface cleaners and turbo nozzles are standard.",
      "Top heavy-duty picks include the Hyundai HYW3100P (petrol), Kärcher HD 9/20-4 M (3-phase), Comet KS 1900 Pro and Nilfisk MC 5M. Trailer hot-water units add diesel burners for grease work.",
      "Expect £700–£8,000+ depending on power source and burner. Worth the investment if your washer earns its keep daily.",
    ],
    faqs: [
      { q: "What makes a pressure washer heavy duty?", a: "Triplex pump, brass head, steel frame, induction or petrol motor, and at least 200 bar. Plastic-pump domestic units don't qualify." },
      { q: "Heavy duty petrol vs heavy duty electric?", a: "Petrol gives mobility and works off-grid; electric is quieter, simpler and cheaper to run if mains is available." },
      { q: "How much does a good heavy duty pressure washer cost?", a: "£700–£1,500 for prosumer; £1,500–£4,000 for full trade; £4,000+ for hot-water trailer-mounted." },
    ],
    amazonQuery: "heavy duty pressure washer",
  },
  {
    slug: "domestic-pressure-washers",
    title: "Domestic Pressure Washers",
    shortLabel: "Domestic",
    group: "type",
    h1: `Best Domestic Pressure Washers UK ${Y}`,
    metaTitle: `Best Domestic Pressure Washers UK ${Y} | Home Reviews`,
    metaDescription:
      "Independent UK reviews of the best domestic pressure washers. Kärcher K2–K7, Nilfisk Core, Bosch UniversalAquatak and budget models compared.",
    intro:
      "Domestic pressure washers cover the vast majority of UK households — quick patio cleans, car washes, garden furniture and the annual driveway scrub.",
    longCopy: [
      "Domestic models run 100–160 bar at 5–8 L/min, all from a standard 13A plug. Kärcher's K-range (K2 through K7) is the dominant family, with the K4 and K5 the best-balanced for typical UK gardens.",
      "Look for induction motors, integrated detergent tanks, hose reels and a clip-on patio cleaner. A 3-year warranty is now standard at £200+.",
      "Expect £80–£500. Don't overbuy: a K5 handles 90% of UK households perfectly — only buy a K7 if you genuinely clean large driveways or rendered walls.",
    ],
    faqs: [
      { q: "What's the best domestic pressure washer?", a: "For most UK households, the Kärcher K5 Premium Smart Control. For budget buyers, the Nilfisk Core 125." },
      { q: "How much should I spend on a domestic pressure washer?", a: "£150–£300 buys a quality 8–10 year machine. Below £100 you compromise on pump life; above £400 is overkill for typical home use." },
      { q: "Best domestic pressure washer for patios?", a: "Any 130+ bar model with a surface-cleaner attachment — the Kärcher K4 Power Control hits the sweet spot." },
    ],
    amazonQuery: "domestic pressure washer",
  },

  // ============ BY USE CASE ============
  {
    slug: "pressure-washers-for-patios",
    title: "Pressure Washers for Patios",
    shortLabel: "Patios",
    group: "use",
    h1: `Best Pressure Washers for Patios UK ${Y}`,
    metaTitle: `Best Pressure Washers for Patios UK ${Y} | Top Picks`,
    metaDescription:
      "The best pressure washers for cleaning patios in the UK. Compare Kärcher K4–K7, Nilfisk Core 140 and patio attachments for fast moss and dirt removal.",
    intro:
      "Cleaning a patio is the single most common reason UK buyers want a pressure washer. Get the spec right and you'll lift years of moss and grime in an afternoon.",
    longCopy: [
      "For typical block paving and Indian sandstone, aim for 130–160 bar at 7+ L/min, paired with a surface-cleaner attachment (a covered rotating-nozzle disc). This combination cleans 5–10× faster than a fan lance alone and leaves no streaks.",
      "Top patio picks: Kärcher K5 Premium Smart Control (with T7 patio cleaner), Nilfisk Core 140 Pro Patio Bundle and Bosch AdvancedAquatak 160 with surface cleaner. Petrol models suit anything over 100 m² of slabs.",
      "Avoid using turbo nozzles on softer stones like sandstone, limestone and old York paving — they'll pit the surface. Stick to a fan lance or surface cleaner.",
    ],
    faqs: [
      { q: "What pressure washer for cleaning patios?", a: "130–160 bar with a surface-cleaner attachment. The Kärcher K5/K7 and Nilfisk Core 140/150 are the UK best-sellers for this." },
      { q: "How do you clean a patio fast with a pressure washer?", a: "Use a surface cleaner (T7 or equivalent), pre-soak with a pressure-washer-rated patio cleaner, then work in straight overlapping passes." },
      { q: "Will a pressure washer damage my patio?", a: "Only if you use a turbo nozzle on soft stone or hold too close. Stick to a fan lance or surface cleaner at 15–20 cm distance." },
    ],
    amazonQuery: "pressure washer for patio",
  },
  {
    slug: "pressure-washers-for-driveways",
    title: "Pressure Washers for Driveways",
    shortLabel: "Driveways",
    group: "use",
    h1: `Best Pressure Washers for Driveways UK ${Y}`,
    metaTitle: `Best Pressure Washers for Driveways UK ${Y} | Block Paving Picks`,
    metaDescription:
      "The best pressure washers for UK driveways. Compare powerful Kärcher K7, Nilfisk Pro and petrol models with surface cleaners for fast block paving cleaning.",
    intro:
      "Driveways need more pressure and a wider surface cleaner than patios — typical UK driveways run 30–80 m² with heavy oil and moss build-up.",
    longCopy: [
      "Aim for 150+ bar at 8–10 L/min and a 30 cm+ surface-cleaner attachment. Petrol washers shine here — a Hyundai HYW3100P at 220 bar will clean a typical drive in under 90 minutes.",
      "Don't forget the chemistry: apply a pressure-washer patio/driveway cleaner for 10 minutes before rinsing. This dissolves moss and biofilm so you're not just blasting at it.",
      "Re-sand block paving joints after every deep clean to prevent slabs from rocking. Standard kiln-dried sand from any builder's merchant works perfectly.",
    ],
    faqs: [
      { q: "How long does it take to pressure wash a driveway?", a: "With a 30 cm surface cleaner and 150+ bar, around 60–90 minutes for a typical UK two-car drive." },
      { q: "What pressure for cleaning block paving?", a: "150–220 bar with a fan lance or surface cleaner. Avoid turbo nozzles which dislodge jointing sand." },
      { q: "Petrol or electric pressure washer for driveway?", a: "Electric (150 bar+) is fine for most drives. Petrol pays off only for large drives (80+ m²) or weekly commercial cleans." },
    ],
    amazonQuery: "pressure washer for driveway",
  },
  {
    slug: "pressure-washers-for-cars",
    title: "Pressure Washers for Cars",
    shortLabel: "Cars",
    group: "use",
    h1: `Best Pressure Washers for Cars UK ${Y}`,
    metaTitle: `Best Pressure Washers for Cars UK ${Y} | Detailer Picks`,
    metaDescription:
      "The best pressure washers for car cleaning in the UK. Compare paint-safe Kärcher, Nilfisk and Bosch models with snow foam lances for swirl-free results.",
    intro:
      "A pressure washer transforms how you wash a car — pre-rinse with snow foam, lift grit, and rinse without ever scratching paintwork with a sponge.",
    longCopy: [
      "Detailers prefer around 110–140 bar at 6–8 L/min — enough pressure to lift dirt but gentle on paint, trim and seals. Most UK detailers run the Kärcher K4/K5, Nilfisk Core 140 or AVA Master P55.",
      "Pair the washer with a quality snow-foam lance (MTM PF22.2 or Karcher Connect & Clean) and a pH-neutral foam like BilberryWheels or Auto Finesse Avalanche.",
      "Avoid turbo nozzles near paintwork — they're for masonry, not detailing. Keep a 30 cm stand-off distance and never spray seals or trim at close range.",
    ],
    faqs: [
      { q: "Is a pressure washer safe for cars?", a: "Yes, at sensible distance (30 cm+) and with a fan or wide-angle nozzle. Avoid turbo nozzles on paint." },
      { q: "What pressure washer for snow foam?", a: "Any model with a Karcher-style or 1/4″ quick-connect outlet — the K2 to K7 all accept third-party snow-foam lances." },
      { q: "Karcher K4 or K5 for car detailing?", a: "K5 has the higher flow (8 L/min) which gives better foam density. K4 is fine if you're occasional rather than weekly." },
    ],
    amazonQuery: "pressure washer for car",
  },
  {
    slug: "pressure-washers-for-bikes",
    title: "Pressure Washers for Bikes",
    shortLabel: "Bikes",
    group: "use",
    h1: `Best Pressure Washers for Bikes UK ${Y}`,
    metaTitle: `Best Pressure Washers for Bikes UK ${Y} | MTB & Road Picks`,
    metaDescription:
      "Top pressure washers for cleaning mountain bikes, road bikes and motorbikes in the UK. Gentle, portable picks from Worx, Bosch, Mac and Karcher.",
    intro:
      "Bicycles, mountain bikes and motorbikes need gentle pressure to lift mud without driving water past bearing seals. Portable cordless washers are usually the best tool.",
    longCopy: [
      "For bikes, look for 20–50 bar at a high flow rate — the Worx Hydroshot, Bosch Fontus, Karcher OC 3 and Muc-Off Pressure Washer are all purpose-designed.",
      "Mains washers (Karcher K2/K3) work too if you set them to a wide fan and stay 30 cm+ from hubs, headset bearings and BB shells. Never aim straight at seals.",
      "Pair with a bike-specific cleaner (Muc-Off Nano Tech, Fenwick's FS-1) for chain-friendly degreasing.",
    ],
    faqs: [
      { q: "Can I use a pressure washer on my bike?", a: "Yes, with care. Use low pressure, a wide nozzle, and never aim at sealed bearings (BB, headset, hubs, suspension)." },
      { q: "Best pressure washer for MTB?", a: "Muc-Off Pressure Washer or Worx Hydroshot — both designed specifically for bike use with safe pressure ranges." },
      { q: "Will pressure washing damage bike bearings?", a: "Only if you blast them at close range with high pressure. Wide fan + 30 cm distance + low pressure is safe." },
    ],
    amazonQuery: "pressure washer for bikes",
  },
  {
    slug: "pressure-washers-for-decking",
    title: "Pressure Washers for Decking",
    shortLabel: "Decking",
    group: "use",
    h1: `Best Pressure Washers for Decking UK ${Y}`,
    metaTitle: `Best Pressure Washers for Decking UK ${Y} | Wood-Safe Picks`,
    metaDescription:
      "The best pressure washers for cleaning timber and composite decking. UK-tested picks with the right pressure to lift algae without damaging soft wood.",
    intro:
      "Decking needs careful pressure — too much will fur softwood grain and ruin the surface. The right model and technique restores decking like new without damage.",
    longCopy: [
      "Aim for 100–130 bar at 6–8 L/min and stick to a 25° fan nozzle or surface-cleaner attachment. Never use a turbo (rotary) nozzle on softwood — it will pit and gouge.",
      "Best picks: Kärcher K4 Power Control (with Wood mode), Nilfisk Core 125 and Bosch UniversalAquatak 135 — all gentle enough for soft pine while still lifting algae.",
      "For composite decking, drop pressure further and follow the manufacturer's max-PSI guidance. Always pre-treat with a deck cleaner to dissolve the algae rather than blasting it off.",
    ],
    faqs: [
      { q: "What pressure for cleaning a decking?", a: "100–130 bar with a fan nozzle, held 20–30 cm from softwood. Composite needs lower pressure — check the deck maker's guide." },
      { q: "Will a pressure washer damage decking?", a: "Yes, if you use a turbo nozzle or hold too close. A fan lance or surface cleaner at moderate pressure is safe on most timbers." },
      { q: "How often should I pressure wash decking?", a: "Once a year in spring, after a winter of algae growth. Pre-treat with a deck cleaner for best results." },
    ],
    amazonQuery: "pressure washer for decking",
  },
  {
    slug: "pressure-washers-for-garden-furniture",
    title: "Pressure Washers for Garden Furniture",
    shortLabel: "Garden Furniture",
    group: "use",
    h1: `Best Pressure Washers for Garden Furniture UK ${Y}`,
    metaTitle: `Best Pressure Washers for Garden Furniture UK ${Y} | Picks`,
    metaDescription:
      "The best pressure washers for cleaning UK garden furniture. Lift mildew from rattan, plastic and teak with the right pressure and nozzle.",
    intro:
      "Garden furniture — rattan, plastic, teak, aluminium — picks up grime and mildew over a UK winter. A low-to-mid pressure washer brings it back to new in minutes.",
    longCopy: [
      "For most furniture, 100–130 bar is plenty. Stay 30 cm+ away on woven rattan to avoid fraying strands. Use a fan lance and pair with a mild detergent.",
      "Top picks: Kärcher K2/K3, Nilfisk Core 125 and Bosch EasyAquatak — all compact, easy to store and easy to deploy for a spring clean.",
      "Never use a turbo nozzle on plastic furniture or rattan weave. For teak, follow with a teak oil treatment to restore colour after cleaning.",
    ],
    faqs: [
      { q: "Can I pressure wash rattan furniture?", a: "Yes, on low pressure (100 bar or less) with a fan nozzle held 30 cm away. Avoid turbo nozzles entirely." },
      { q: "Best pressure washer for plastic garden furniture?", a: "Any compact 110–125 bar electric model with a fan lance — Kärcher K2 or Nilfisk Core 125." },
      { q: "Will pressure washing damage teak furniture?", a: "Only at high pressure or close range. Low pressure plus a fan lance lifts grime safely; re-oil afterwards." },
    ],
    amazonQuery: "pressure washer for garden furniture",
  },
  {
    slug: "pressure-washers-for-roof-cleaning",
    title: "Pressure Washers for Roof Cleaning",
    shortLabel: "Roofs",
    group: "use",
    h1: `Best Pressure Washers for Roof Cleaning UK ${Y}`,
    metaTitle: `Best Pressure Washers for Roof Cleaning UK ${Y} | Soft Wash`,
    metaDescription:
      "Independent UK guide to pressure washers for roof cleaning. Soft-wash and low-pressure picks plus moss-removal advice for tiles and slates.",
    intro:
      "Roof cleaning is a special case — high pressure will dislodge tiles and force water under laps. Modern best practice is soft wash with biocide and low pressure.",
    longCopy: [
      "Soft-wash systems run at under 30 bar with a long lance and apply a biocide that kills moss over weeks. Top picks include the Karcher SC4 and dedicated soft-wash systems from SpinAclean or Wash-IT.",
      "If you must use a pressure washer on a roof, stay at low pressure (under 100 bar), use a wide fan nozzle, and never spray uphill or against the laps.",
      "For severely mossed roofs, scrape first with a roof scraper, soft-wash with biocide, then leave to die back over several months. Pressure washing alone is rarely the right answer.",
    ],
    faqs: [
      { q: "Can you pressure wash a roof?", a: "Yes, but only at low pressure and with care. Soft washing with biocide is the industry-preferred method." },
      { q: "Will a pressure washer damage roof tiles?", a: "Yes — high pressure dislodges the protective surface coating, lifts mortar and can force water under laps. Stick to under 100 bar." },
      { q: "What kills moss on roofs UK?", a: "A biocide-based moss killer (e.g. SoftWash Systems, Wet & Forget) applied via a soft-wash lance, left to work over weeks." },
    ],
    amazonQuery: "pressure washer for roof",
  },
  {
    slug: "pressure-washers-for-moss-removal",
    title: "Pressure Washers for Moss Removal",
    shortLabel: "Moss Removal",
    group: "use",
    h1: `Best Pressure Washers for Moss Removal UK ${Y}`,
    metaTitle: `Best Pressure Washers for Moss Removal UK ${Y} | Top Picks`,
    metaDescription:
      "The best pressure washers for moss removal in the UK. Compare Kärcher, Nilfisk and petrol picks to clear moss from patios, driveways, paths and walls.",
    intro:
      "Moss thrives in damp shaded UK gardens — on paths, patios, driveways and walls. A pressure washer with the right attachment lifts a year's growth in minutes.",
    longCopy: [
      "Aim for 130+ bar at 8 L/min minimum. Pair with a surface-cleaner attachment for flat areas (driveways, patios) and a fan or turbo lance for vertical walls and steps.",
      "Pre-treat with a biocide moss killer (Patio Magic, Wet & Forget) 24 hours before washing — this lifts roots and prevents fast regrowth.",
      "After cleaning, re-sand block paving joints with kiln-dried sand. Without re-sanding, moss returns within months and slabs start to rock.",
    ],
    faqs: [
      { q: "What pressure to remove moss?", a: "130+ bar with a turbo nozzle or surface cleaner. Pre-treating with a biocide kills the roots so the moss doesn't return quickly." },
      { q: "Will a pressure washer remove moss permanently?", a: "Only if combined with a biocide and re-sanding of joints. Pressure alone removes the green but the spores remain in the substrate." },
      { q: "Best pressure washer for moss removal UK?", a: "Kärcher K5 Premium Smart Control or Nilfisk Core 140 with a surface cleaner. Petrol Hyundai HYW3100P for very large areas." },
    ],
    amazonQuery: "pressure washer for moss removal",
  },
  {
    slug: "pressure-washers-for-caravan-cleaning",
    title: "Pressure Washers for Caravan Cleaning",
    shortLabel: "Caravans",
    group: "use",
    h1: `Best Pressure Washers for Caravan Cleaning UK ${Y}`,
    metaTitle: `Best Pressure Washers for Caravan Cleaning UK ${Y} | Picks`,
    metaDescription:
      "The best pressure washers for cleaning caravans, motorhomes and campervans in the UK. Portable mains and battery picks plus safe washing technique.",
    intro:
      "Caravans pick up grit, road film and algae stripes over a season. A medium-pressure portable washer makes spring cleaning easy without damaging seals or decals.",
    longCopy: [
      "Stick to 100–130 bar with a fan lance. Avoid turbo nozzles near windows, seals, vents and decals — they'll force water past seals and lift graphics.",
      "Best caravan picks: Kärcher K4 with extension lance, Nilfisk Core 130 and the Bosch Fontus battery washer for campsite use where mains isn't available.",
      "Pair with a caravan-safe shampoo (Fenwick's Caravan Cleaner) and a soft brush for the roof — the most-neglected and dirtiest part of any caravan.",
    ],
    faqs: [
      { q: "Can I pressure wash a caravan?", a: "Yes — at low to mid pressure (under 130 bar) with a fan lance. Avoid turbo nozzles and stay 30 cm+ from seals." },
      { q: "What's the best portable pressure washer for caravans?", a: "Bosch Fontus battery washer or Kärcher OC 3 for campsite use; a mid-size mains K4 at home." },
      { q: "Will pressure washing damage caravan decals?", a: "It can — high pressure or close range will lift the edges. Use wide fan + 30 cm+ stand-off and they'll be fine." },
    ],
    amazonQuery: "pressure washer for caravan",
  },
  {
    slug: "pressure-washers-for-horse-trailers",
    title: "Pressure Washers for Horse Trailers",
    shortLabel: "Horse Trailers",
    group: "use",
    h1: `Best Pressure Washers for Horse Trailers UK ${Y}`,
    metaTitle: `Best Pressure Washers for Horse Trailers UK ${Y} | Equestrian Picks`,
    metaDescription:
      "UK guide to the best pressure washers for cleaning horse trailers, horseboxes and stable yards. Compare hot-water, petrol and electric options.",
    intro:
      "Horse trailers and horseboxes get muddy, manured and salt-corroded. The right pressure washer cuts cleaning time dramatically and helps prevent rust.",
    longCopy: [
      "For a quick muck-out, 130–150 bar at 8 L/min is plenty. For deep cleaning after a long event season, a hot-water washer dissolves grease and manure stains far better.",
      "Top picks: Kärcher K5/K7 (electric domestic), Hyundai HYW3100P (petrol mobile), Kärcher HDS 5/12 (hot-water trade). Pair with an equine-safe disinfectant after cleaning.",
      "Clean the wheel arches, axle and floor channels regularly — road salt is the biggest killer of horse trailers over winter.",
    ],
    faqs: [
      { q: "Hot or cold water pressure washer for a horse trailer?", a: "Cold is fine for routine cleaning. Hot water shines for post-season deep cleans and removing dried-on manure." },
      { q: "Best pressure washer for equestrian use?", a: "Kärcher K7 for occasional use; Hyundai HYW3100P petrol for off-mains yards; Kärcher HDS 5/12 hot-water for serious yards." },
      { q: "Can I pressure wash with disinfectant?", a: "Most disinfectants apply via the washer's detergent tank — check the chemical is rated for low-pressure injection." },
    ],
    amazonQuery: "pressure washer for horse trailer",
  },
  {
    slug: "pressure-washers-for-boats",
    title: "Pressure Washers for Boats",
    shortLabel: "Boats",
    group: "use",
    h1: `Best Pressure Washers for Boats UK ${Y}`,
    metaTitle: `Best Pressure Washers for Boats UK ${Y} | Marine Picks`,
    metaDescription:
      "Independent UK guide to pressure washers for cleaning boats, dinghies, yachts and trailers. Compare freshwater-safe models for gel coat and antifouling.",
    intro:
      "Salt, algae and antifoul make boats tough to clean. A 130–150 bar washer plus a freshwater rinse extends gel coat life and keeps your hull race-ready.",
    longCopy: [
      "For gel coat and hull washing, 110–140 bar at 8 L/min is the sweet spot. Avoid turbo nozzles which can dull and pit gel coat. Stay 30 cm+ from antifoul to avoid stripping the coating.",
      "Top picks: Kärcher K5 Premium, Nilfisk Core 140 (occasional), and Kärcher HD 5/15 (marina-grade for trade). A petrol washer is essential for moorings without electricity.",
      "Always rinse with fresh water before pressure washing to remove gritty salt, then follow with a marine wash and wax to seal the surface.",
    ],
    faqs: [
      { q: "Can I pressure wash a boat hull?", a: "Yes — at moderate pressure with a fan lance. Avoid turbo nozzles and respect minimum distance to antifoul." },
      { q: "Best pressure washer for boats?", a: "Kärcher K5 for trailers and dinghies; petrol Hyundai HYW3100P for moorings; Kärcher HD 5/15 for marina trade use." },
      { q: "Will pressure washing damage gel coat?", a: "Only if you use a turbo nozzle or hold too close. Wide fan + 30 cm distance is safe and effective." },
    ],
    amazonQuery: "pressure washer for boat",
  },
  {
    slug: "pressure-washers-for-agricultural-equipment",
    title: "Pressure Washers for Agricultural Equipment",
    shortLabel: "Agricultural",
    group: "use",
    h1: `Best Pressure Washers for Agricultural Equipment UK ${Y}`,
    metaTitle: `Best Pressure Washers for Agricultural Equipment UK ${Y} | Farm`,
    metaDescription:
      "The best pressure washers for cleaning tractors, ploughs, combines and farm machinery in the UK. Petrol, diesel and hot-water options compared.",
    intro:
      "Tractors, ploughs and combines pick up clay, manure, oil and chemical residue. Heavy-duty petrol and hot-water washers are essential farm yard kit.",
    longCopy: [
      "For routine cleaning, 200+ bar petrol washers like the Hyundai HYW3100P, Honda-powered SwitZer and SIP Tempest handle mud and chaff easily. For grease and oil, step up to a hot-water diesel-burner unit (Kärcher HDS, Nilfisk MH).",
      "Trailer-mounted hot-water washers with onboard tanks are the gold standard for mixed farms — clean any machine, anywhere on the holding, without site water or mains.",
      "Pair with an agricultural-grade detergent (Lava Pro, Sealey ASE12.S) for clay-clinging mud and silage residue.",
    ],
    faqs: [
      { q: "What pressure washer for tractors?", a: "200+ bar petrol for routine; hot-water diesel-burner units for grease, oil and silage residue." },
      { q: "Hot water vs cold water for farm machinery?", a: "Hot water dramatically reduces cleaning time on grease and silage. Cold water with petrol power is fine for mud and chaff." },
      { q: "Best farm pressure washer UK?", a: "Hyundai HYW3100P (cold petrol); Kärcher HDS 7/16 (hot-water electric); Kärcher HDS Trailer (mobile hot-water diesel)." },
    ],
    amazonQuery: "pressure washer for agricultural",
  },
  {
    slug: "pressure-washers-for-brick-cleaning",
    title: "Pressure Washers for Brick Cleaning",
    shortLabel: "Brickwork",
    group: "use",
    h1: `Best Pressure Washers for Brick Cleaning UK ${Y}`,
    metaTitle: `Best Pressure Washers for Brick Cleaning UK ${Y} | Top Picks`,
    metaDescription:
      "Independent UK guide to the best pressure washers for brick cleaning. Tackle algae, lichen and pollution from house walls, garden walls and patios.",
    intro:
      "Brick walls, especially north-facing ones, attract algae, lichen and pollution staining. A mid-power pressure washer with a fan or turbo lance restores them beautifully.",
    longCopy: [
      "For sound modern brickwork, 130–160 bar is fine. For old soft-brick walls (pre-1900) drop pressure to under 100 bar and stay 30 cm+ away to avoid eroding mortar.",
      "Pre-treat with an exterior cleaner (Wet & Forget, Patio Magic) and leave for 24 hours before rinsing. Repointing may be needed afterwards on older walls.",
      "Top picks: Kärcher K5 Premium, Bosch AdvancedAquatak 160 with surface cleaner, and Hyundai HYW3100P petrol for larger walls.",
    ],
    faqs: [
      { q: "Can you pressure wash a brick house?", a: "Yes — at moderate pressure (130–160 bar for modern brick; under 100 bar for pre-1900). Avoid turbo nozzles on soft brick and old mortar." },
      { q: "Will pressure washing damage bricks?", a: "Modern engineering and facing bricks are fine. Soft handmade bricks and lime-mortar walls can erode — use low pressure and a fan lance." },
      { q: "Best way to clean algae off brick walls?", a: "Pre-treat with biocide, leave 24 hours, then rinse at moderate pressure. Don't blast at it — kill it first." },
    ],
    amazonQuery: "pressure washer for brickwork",
  },
  {
    slug: "pressure-washers-for-conservatories",
    title: "Pressure Washers for Conservatories",
    shortLabel: "Conservatories",
    group: "use",
    h1: `Best Pressure Washers for Conservatories UK ${Y}`,
    metaTitle: `Best Pressure Washers for Conservatories UK ${Y} | Roof & Glass`,
    metaDescription:
      "The best pressure washers for cleaning conservatory roofs, glass and uPVC. Low-pressure picks plus telescopic lance recommendations for UK homes.",
    intro:
      "Conservatory roofs collect algae, leaves and pollution faster than most parts of the house. A low-pressure washer plus a telescopic lance makes the job safe from the ground.",
    longCopy: [
      "Use 100–125 bar maximum on polycarbonate, glass and uPVC. Pair with a 7m+ telescopic lance (Karcher TLA 7) to reach roof panels safely from ground level.",
      "Top picks: Kärcher K3 with telescopic lance, Nilfisk Core 125 and Bosch UniversalAquatak 135. For soft-wash chemistry, use Wet & Forget Outdoor as a pre-treat.",
      "Always pre-wet seals and silicone joints, and never aim at vent ridges — water can be driven into the eaves.",
    ],
    faqs: [
      { q: "Can I pressure wash a conservatory roof?", a: "Yes — low pressure (under 125 bar), fan lance, telescopic extension. Pre-treat with a biocide for best results." },
      { q: "Will pressure washing damage polycarbonate?", a: "Not at low pressure. Avoid turbo nozzles which can craze the surface." },
      { q: "Best pressure washer for conservatory roof?", a: "Kärcher K3/K4 with TLA telescopic lance, or a soft-wash system for severely algae-covered roofs." },
    ],
    amazonQuery: "pressure washer for conservatory",
  },
  {
    slug: "pressure-washers-for-bin-cleaning",
    title: "Pressure Washers for Bin Cleaning",
    shortLabel: "Bins",
    group: "use",
    h1: `Best Pressure Washers for Bin Cleaning UK ${Y}`,
    metaTitle: `Best Pressure Washers for Bin Cleaning UK ${Y} | Picks`,
    metaDescription:
      "The best pressure washers for cleaning wheelie bins, food caddies and recycling bins in the UK. Hot-water and disinfectant compatibility compared.",
    intro:
      "Wheelie bins build up bacteria, smells and stains over the year. A pressure washer plus disinfectant turns the worst bin spotless in two minutes.",
    longCopy: [
      "Domestic bin cleaning works fine with a 110–130 bar electric washer plus a bin-specific disinfectant. For commercial bin-cleaning services, hot-water trailer units (Karcher HDS) are the industry standard.",
      "Top picks: Kärcher K3, Nilfisk Core 125 (domestic); Kärcher HDS 5/12 (commercial). Pair with bin sanitiser like SmartSeal or Ecover.",
      "Always wash bins on a porous surface or over a drain — never on a public road or path due to discharge regulations.",
    ],
    faqs: [
      { q: "How do I clean a wheelie bin with a pressure washer?", a: "Tip the bin on its side, spray inside walls and base, apply a bin-safe disinfectant, then rinse. Let it air-dry upside down." },
      { q: "Hot or cold water for bin cleaning?", a: "Cold + disinfectant works for domestic. Hot water is much faster for daily commercial bin cleaning." },
      { q: "Best pressure washer for bin cleaning business?", a: "Karcher HDS hot-water trailer unit — the industry standard for commercial bin cleaning rounds." },
    ],
    amazonQuery: "pressure washer for bin cleaning",
  },
  {
    slug: "pressure-washers-for-gutters",
    title: "Pressure Washers for Gutters",
    shortLabel: "Gutters",
    group: "use",
    h1: `Best Pressure Washers for Gutters UK ${Y}`,
    metaTitle: `Best Pressure Washers for Gutters UK ${Y} | Cleaning Picks`,
    metaDescription:
      "The best pressure washers and gutter-cleaning attachments for UK homes. Telescopic lances, gutter wands and safe ground-level cleaning techniques.",
    intro:
      "Clearing gutters from a ladder is the most dangerous DIY job in the UK. A pressure washer with a telescopic gutter lance lets you do it safely from the ground.",
    longCopy: [
      "Pair a 130+ bar washer with a gutter-cleaning attachment (Karcher Gutter & Pipe Cleaning Kit or a J-shaped wand) and a 4–7m telescopic lance. Most jobs need under 100 bar at the nozzle.",
      "Top picks: Kärcher K5 with Gutter Kit, Nilfisk Core 140 with telescopic lance, and the Bosch AdvancedAquatak 160 with extension lance.",
      "Don't forget to flush downpipes too — blockages there are the most common cause of overflow damage.",
    ],
    faqs: [
      { q: "Can a pressure washer clean gutters?", a: "Yes — with a gutter-cleaning attachment and telescopic lance, you can clean from ground level in under 30 minutes." },
      { q: "What's the best pressure washer attachment for gutters?", a: "A J-shaped gutter wand or the Karcher Gutter Cleaning Kit — both designed to hook over the gutter edge and direct spray along the channel." },
      { q: "How often should I pressure wash gutters?", a: "Once in late autumn after leaf fall, and once in late spring to clear winter debris and any moss build-up." },
    ],
    amazonQuery: "pressure washer for gutter cleaning",
  },
  {
    slug: "pressure-washers-for-fencing",
    title: "Pressure Washers for Fencing",
    shortLabel: "Fencing",
    group: "use",
    h1: `Best Pressure Washers for Fencing UK ${Y}`,
    metaTitle: `Best Pressure Washers for Fencing UK ${Y} | Cleaning Picks`,
    metaDescription:
      "The best pressure washers for cleaning garden fences in the UK. Lift algae and old stain from larch lap, featheredge and close-board panels.",
    intro:
      "Garden fences go green and grimy fast in a UK climate. A pressure washer strips algae, peeling stain and dirt in minutes — ready for fresh treatment.",
    longCopy: [
      "Use 120–140 bar at a fan or 25° nozzle. Avoid turbo nozzles which fur softwood grain. Work top-down, with overlapping strokes, 20–30 cm from the panel.",
      "Top picks: Kärcher K4 Power Control (with Wood mode), Nilfisk Core 125, Bosch UniversalAquatak 135. For very large boundaries, a petrol Hyundai HYW3100P pays back fast.",
      "Pre-treat green panels with a fence cleaner (Cuprinol Decking & Fence Cleaner) for fastest results. Re-treat with a stain after the wood dries fully (48+ hours).",
    ],
    faqs: [
      { q: "Will a pressure washer damage a wooden fence?", a: "Not with a fan nozzle at moderate pressure. Avoid turbo nozzles on soft pine — they'll raise the grain and pit the surface." },
      { q: "Best pressure washer for fence cleaning?", a: "Mid-size electric models with Wood/Patio modes (Karcher K4 Power Control, Nilfisk Core 130) — or petrol Hyundai HYW3100P for large boundaries." },
      { q: "How do you clean a fence before staining?", a: "Pressure wash with a fan lance, allow to dry 48 hours, then apply your stain or treatment. Most stain failures trace back to applying on damp wood." },
    ],
    amazonQuery: "pressure washer for fence",
  },
  {
    slug: "pressure-washers-for-render-cleaning",
    title: "Pressure Washers for Render Cleaning",
    shortLabel: "Render",
    group: "use",
    h1: `Best Pressure Washers for Render Cleaning UK ${Y}`,
    metaTitle: `Best Pressure Washers for Render Cleaning UK ${Y} | Top Picks`,
    metaDescription:
      "The best pressure washers for cleaning rendered house walls in the UK. Compare soft-wash and low-pressure picks for algae, dirt and stains.",
    intro:
      "Rendered walls — pebbledash, K-rend, monocouche — collect algae and pollution staining. The right wash gives years of fresh appearance without re-rendering.",
    longCopy: [
      "Best practice is low-pressure soft wash with biocide rather than blasting. Use a 100–130 bar washer with a wide fan, paired with a render-safe cleaner (Wash-IT, Wet & Forget).",
      "Top picks: Kärcher K4/K5 with telescopic lance, Nilfisk Core 140 Pro, and Karcher SC4 soft-wash system. Avoid turbo nozzles which can erode older renders.",
      "Always pre-wet the wall, work top-down, and treat algae stains chemically rather than mechanically for best long-term results.",
    ],
    faqs: [
      { q: "Can I pressure wash render?", a: "Yes — at low to moderate pressure with a fan lance. Pre-treat with biocide for best results." },
      { q: "Will pressure washing damage render?", a: "It can on older soft renders or chipped K-rend. Stick to under 130 bar and a fan nozzle to be safe." },
      { q: "Best way to clean rendered walls UK?", a: "Soft wash with biocide, leave 24 hours, low-pressure rinse. Better than blasting — and lasts longer." },
    ],
    amazonQuery: "pressure washer for render cleaning",
  },
  {
    slug: "pressure-washers-for-patio-slabs",
    title: "Pressure Washers for Patio Slabs",
    shortLabel: "Patio Slabs",
    group: "use",
    h1: `Best Pressure Washers for Patio Slabs UK ${Y}`,
    metaTitle: `Best Pressure Washers for Patio Slabs UK ${Y} | Picks`,
    metaDescription:
      "The best pressure washers for cleaning Indian sandstone, concrete and porcelain patio slabs in the UK. Stone-safe pressure and surface cleaner picks.",
    intro:
      "Patio slabs — sandstone, limestone, porcelain, concrete — each respond differently to pressure washing. Match technique to stone for streak-free results.",
    longCopy: [
      "For hard concrete and porcelain, 150+ bar with a surface-cleaner attachment is perfect. For soft Indian sandstone, drop to 130 bar and avoid turbo nozzles which pit the surface.",
      "Top picks: Kärcher K5/K7 with T7 surface cleaner, Nilfisk Core 150 Pro, Bosch AdvancedAquatak 160. Pre-treat with a stone-safe patio cleaner for fastest results.",
      "After cleaning, apply a patio sealer to lock out future algae growth — especially valuable on porous sandstone.",
    ],
    faqs: [
      { q: "Can you pressure wash sandstone slabs?", a: "Yes, at moderate pressure with a fan or surface cleaner. Avoid turbo nozzles on soft sandstone." },
      { q: "What pressure for porcelain slabs?", a: "150+ bar with a surface cleaner — porcelain is extremely hard-wearing and won't be damaged by domestic pressure washers." },
      { q: "Best pressure washer for patio slabs?", a: "Kärcher K5/K7 with surface-cleaner attachment is the UK best-seller for patio work." },
    ],
    amazonQuery: "pressure washer for patio slabs",
  },
  {
    slug: "pressure-washers-for-outdoor-tiles",
    title: "Pressure Washers for Outdoor Tiles",
    shortLabel: "Outdoor Tiles",
    group: "use",
    h1: `Best Pressure Washers for Outdoor Tiles UK ${Y}`,
    metaTitle: `Best Pressure Washers for Outdoor Tiles UK ${Y} | Top Picks`,
    metaDescription:
      "The best pressure washers for cleaning outdoor tiles in the UK. Porcelain, ceramic and natural-stone tile picks plus safe pressure guidance.",
    intro:
      "Outdoor tiles — porcelain, ceramic, stone — need a balance of pressure and care. The right washer restores them without damaging grout or surface coatings.",
    longCopy: [
      "For porcelain and ceramic, 130–160 bar with a fan lance or surface cleaner is ideal. For natural stone (limestone, travertine), drop to 100–125 bar and use a wide fan only.",
      "Top picks: Kärcher K5 Premium, Nilfisk Core 140, Bosch UniversalAquatak 135. Combine with a tile-safe cleaner like HG Outdoor Tile Cleaner.",
      "Don't blast directly at grout lines — they'll erode under sustained high pressure. Work parallel to the joints and keep moving.",
    ],
    faqs: [
      { q: "Can you pressure wash outdoor tiles?", a: "Yes — at moderate pressure with a fan lance. Avoid pointing directly at grout lines for long periods." },
      { q: "What pressure for porcelain outdoor tiles?", a: "130–160 bar is fine. Porcelain is one of the toughest outdoor surfaces and stands up well to domestic pressure washers." },
      { q: "Will pressure washing damage outdoor tile grout?", a: "It can if you concentrate flow on the joints. Work parallel to the grout and keep moving." },
    ],
    amazonQuery: "pressure washer for outdoor tiles",
  },

  // ============ BY PRESSURE / PERFORMANCE ============
  {
    slug: "110-bar-pressure-washers",
    title: "110 Bar Pressure Washers",
    shortLabel: "110 Bar",
    group: "pressure",
    h1: `Best 110 Bar Pressure Washers UK ${Y}`,
    metaTitle: `Best 110 Bar Pressure Washers UK ${Y} | Compact Picks`,
    metaDescription:
      "The best 110 bar pressure washers in the UK. Compact entry-level mains models for cars, bikes and small patios.",
    intro:
      "110 bar pressure washers are the popular entry-level spec for occasional use — washing cars, bikes, garden furniture and small patios.",
    longCopy: [
      "110 bar (roughly 1,600 PSI) at 5–6 L/min handles light to moderate household cleaning. Most are around 1400W and weigh under 7 kg.",
      "Top picks: Kärcher K2 Premium Full Control, Nilfisk Core 110, Bosch EasyAquatak 110. Expect £80–£130.",
      "For driveway and moss work step up to 130+ bar. For cars, bikes and weekly small patio cleans, 110 bar is enough.",
    ],
    faqs: [
      { q: "Is 110 bar enough to clean a patio?", a: "For light moss yes, with a surface cleaner. For ingrained moss and large drives, 130+ bar is much faster." },
      { q: "What can a 110 bar pressure washer do?", a: "Cars, bikes, garden furniture, small patios, plastic playhouses, garden tools — all comfortably." },
      { q: "Best 110 bar pressure washer UK?", a: "Kärcher K2 Premium Full Control for build quality; Nilfisk Core 110 for value." },
    ],
    amazonQuery: "110 bar pressure washer",
  },
  {
    slug: "120-bar-pressure-washers",
    title: "120 Bar Pressure Washers",
    shortLabel: "120 Bar",
    group: "pressure",
    h1: `Best 120 Bar Pressure Washers UK ${Y}`,
    metaTitle: `Best 120 Bar Pressure Washers UK ${Y} | Mid-Range Picks`,
    metaDescription:
      "The best 120 bar pressure washers in the UK. Sweet-spot mains models for cars, decking and small driveways from Kärcher, Nilfisk and Bosch.",
    intro:
      "120 bar washers sit in the value sweet spot — enough power for decking and small drives without paying for pro-grade flow rates.",
    longCopy: [
      "Expect 1700W induction or universal motors, 6 L/min flow and 8–10 m hose. Decent for occasional driveway clean-ups and weekly patio work.",
      "Top picks: Kärcher K3 Full Control, Nilfisk Core 125-5, Bosch EasyAquatak 120. Price band £100–£170.",
      "Pair with a patio cleaner attachment for fast surface work, or stick with the standard fan lance for cars and garden furniture.",
    ],
    faqs: [
      { q: "120 bar vs 130 bar — does the difference matter?", a: "Marginal. Flow rate and pump quality matter more than 10 bar of headline pressure." },
      { q: "Will 120 bar lift moss from a patio?", a: "Yes, especially with a surface-cleaner attachment and a biocide pre-treatment." },
      { q: "Best 120 bar pressure washer?", a: "Kärcher K3 Full Control combines pressure, hose reel and the Karcher accessory ecosystem." },
    ],
    amazonQuery: "120 bar pressure washer",
  },
  {
    slug: "130-bar-pressure-washers",
    title: "130 Bar Pressure Washers",
    shortLabel: "130 Bar",
    group: "pressure",
    h1: `Best 130 Bar Pressure Washers UK ${Y}`,
    metaTitle: `Best 130 Bar Pressure Washers UK ${Y} | Patio Picks`,
    metaDescription:
      "The best 130 bar pressure washers in the UK. Strong all-rounders for patios, driveways and cars from Kärcher, Nilfisk and Bosch reviewed.",
    intro:
      "130 bar is the practical minimum for serious patio and driveway cleaning. Most UK households are perfectly served by a 130 bar machine.",
    longCopy: [
      "Expect 1800W induction motors, 7–8 L/min flow, hose reels and surface-cleaner attachments included. Around £150–£250.",
      "Top picks: Kärcher K4 Power Control, Nilfisk Core 130 Pro, Bosch UniversalAquatak 135. All include a patio cleaner and 8m hose.",
      "Step up to 150+ bar only if you've got a very large drive or rendered walls. For typical UK gardens, 130 bar is enough.",
    ],
    faqs: [
      { q: "Is 130 bar a good pressure washer?", a: "Yes — for typical UK homes, 130 bar is the sweet spot. Powerful enough for moss removal but compact and quiet enough for weekend use." },
      { q: "130 bar vs 150 bar?", a: "150 bar is faster on driveways. For most patios and cars, 130 bar with a surface cleaner is plenty." },
      { q: "Best 130 bar pressure washer UK?", a: "Kärcher K4 Power Control — induction motor, hose reel, patio cleaner included." },
    ],
    amazonQuery: "130 bar pressure washer",
  },
  {
    slug: "140-bar-pressure-washers",
    title: "140 Bar Pressure Washers",
    shortLabel: "140 Bar",
    group: "pressure",
    h1: `Best 140 Bar Pressure Washers UK ${Y}`,
    metaTitle: `Best 140 Bar Pressure Washers UK ${Y} | Top Picks`,
    metaDescription:
      "The best 140 bar pressure washers in the UK. Step-up mains models with induction motors and full driveway-cleaning capability.",
    intro:
      "140 bar washers strike the balance between domestic ease and serious cleaning capacity — ideal for medium driveways, walls and decking.",
    longCopy: [
      "Expect 1.8–2.0 kW induction motors, 7.5–8 L/min flow, and accessory bundles including a surface cleaner. Most ship with hose reels and 8m hoses.",
      "Top picks: Kärcher K5 Compact, Nilfisk Core 140 Pro, Bosch AdvancedAquatak 140. Around £200–£280.",
      "This is the right step up if a 110–130 bar model has felt under-powered for your driveway or rendered walls.",
    ],
    faqs: [
      { q: "Is 140 bar enough for a driveway?", a: "Yes — comfortably, with a surface-cleaner attachment. Most UK driveways are well served by 140 bar." },
      { q: "Best 140 bar pressure washer?", a: "Nilfisk Core 140 Pro or Kärcher K5 Compact — both with brass pump heads and 3-year warranty." },
      { q: "140 bar vs 160 bar?", a: "160 bar is faster on big drives. For typical UK use, 140 bar is the sweet spot." },
    ],
    amazonQuery: "140 bar pressure washer",
  },
  {
    slug: "150-bar-pressure-washers",
    title: "150 Bar Pressure Washers",
    shortLabel: "150 Bar",
    group: "pressure",
    h1: `Best 150 Bar Pressure Washers UK ${Y}`,
    metaTitle: `Best 150 Bar Pressure Washers UK ${Y} | Driveway Picks`,
    metaDescription:
      "The best 150 bar pressure washers in the UK. Powerful mains models for full driveway cleaning, brickwork and high-volume household use.",
    intro:
      "150 bar washers are the standard upgrade for large driveways and serious household cleaning — induction motors, brass heads and full accessory bundles.",
    longCopy: [
      "At 150 bar / 9 L/min, you'll clean a typical UK driveway in 45–60 minutes with a 30 cm surface cleaner. Most run on 2.0 kW induction motors with brass pump heads.",
      "Top picks: Kärcher K5 Premium Smart Control, Nilfisk Core 150 Pro, Bosch AdvancedAquatak 150. Expect £250–£400.",
      "Step up to 160+ bar only if you have a very large drive or work in commercial cleaning. 150 bar is plenty for most.",
    ],
    faqs: [
      { q: "Is 150 bar a powerful pressure washer?", a: "Yes — solidly in the prosumer band. Handles full driveways, brickwork and rendered walls easily." },
      { q: "Best 150 bar pressure washer UK?", a: "Kärcher K5 Premium Smart Control — best blend of pressure, smart features and warranty." },
      { q: "150 bar in PSI?", a: "Around 2,175 PSI." },
    ],
    amazonQuery: "150 bar pressure washer",
  },
  {
    slug: "160-bar-pressure-washers",
    title: "160 Bar Pressure Washers",
    shortLabel: "160 Bar",
    group: "pressure",
    h1: `Best 160 Bar Pressure Washers UK ${Y}`,
    metaTitle: `Best 160 Bar Pressure Washers UK ${Y} | High-Power Picks`,
    metaDescription:
      "The best 160 bar pressure washers in the UK. Top-tier mains models for large driveways, rendered walls and serious moss removal.",
    intro:
      "160 bar is the upper limit of mains domestic pressure washers — strongly recommended for large drives, rendered walls and serious moss removal.",
    longCopy: [
      "Expect 2.1–2.5 kW induction motors, 9–10 L/min flow and brass pump heads. Most ship with full accessory bundles including a 30 cm+ surface cleaner.",
      "Top picks: Kärcher K7 Premium Smart Control, Bosch AdvancedAquatak 160 and Nilfisk Excellent 160 Power. Expect £350–£600.",
      "Beyond 160 bar you're into petrol or 3-phase territory. For domestic use, 160 bar is the practical ceiling.",
    ],
    faqs: [
      { q: "Is 160 bar too powerful for a domestic pressure washer?", a: "No, but use the right nozzle — fan or surface cleaner for slabs, not turbo, on softer stones." },
      { q: "Best 160 bar pressure washer UK?", a: "Kärcher K7 Premium Smart Control — top-of-the-line for domestic mains use." },
      { q: "160 bar vs 200 bar?", a: "200 bar requires petrol or 3-phase. 160 bar is the practical limit for a 13A plug-in domestic washer." },
    ],
    amazonQuery: "160 bar pressure washer",
  },
  {
    slug: "high-psi-pressure-washers",
    title: "High PSI Pressure Washers",
    shortLabel: "High PSI",
    group: "pressure",
    h1: `Best High PSI Pressure Washers UK ${Y}`,
    metaTitle: `Best High PSI Pressure Washers UK ${Y} | 2,500+ PSI Picks`,
    metaDescription:
      "The best high PSI pressure washers in the UK. Compare 2,500–4,000 PSI petrol and pro-electric models for heavy-duty cleaning.",
    intro:
      "High PSI pressure washers (2,500+ PSI / 170+ bar) tackle the toughest cleaning jobs — large driveways, rendered walls, agricultural machinery and contractor work.",
    longCopy: [
      "Above 2,500 PSI you're into prosumer or trade territory. Petrol washers like the Hyundai HYW3100P deliver 3,100 PSI; commercial Kärcher HD 5/15 hits 2,175 PSI from a single-phase electric motor.",
      "Top high-PSI picks: Hyundai HYW3100P (3,100 PSI petrol), Karcher HD 5/15 C (2,175 PSI electric), SwitZer SZ17 (2,500 PSI petrol).",
      "Watch the PSI vs flow rate trade-off — high PSI with low GPM is less effective than a balanced mid-PSI / high-flow combination.",
    ],
    faqs: [
      { q: "What's considered high PSI in a pressure washer?", a: "2,500+ PSI (170+ bar). Domestic units typically max out at 2,200 PSI; pro and petrol reach 3,000–4,000 PSI." },
      { q: "Is higher PSI always better?", a: "No — flow rate (GPM/L/min) matters equally. A balanced 2,500 PSI / 8 L/min beats a 4,000 PSI / 4 L/min unit for most surfaces." },
      { q: "Best high PSI pressure washer UK?", a: "Hyundai HYW3100P petrol (3,100 PSI) for off-grid; Kärcher HD 5/15 C electric (2,175 PSI) for trade." },
    ],
    amazonQuery: "high psi pressure washer",
  },
  {
    slug: "high-flow-pressure-washers",
    title: "High Flow Pressure Washers",
    shortLabel: "High Flow",
    group: "pressure",
    h1: `Best High Flow Pressure Washers UK ${Y}`,
    metaTitle: `Best High Flow Pressure Washers UK ${Y} | High L/min Picks`,
    metaDescription:
      "The best high flow pressure washers in the UK. 9–15+ L/min models that clean large areas faster than high-PSI low-flow alternatives.",
    intro:
      "High flow rate (L/min) matters more than headline pressure for cleaning large surfaces fast. Look for 9+ L/min mains and 12+ L/min petrol.",
    longCopy: [
      "Flow rate determines how much water hits the surface per second — driving rinse-off and cleaning speed. A 9 L/min 130 bar washer clears a driveway faster than a 6 L/min 160 bar machine.",
      "Top high-flow picks: Kärcher K7 (9 L/min), Nilfisk Excellent 160 Power (10 L/min), petrol Hyundai HYW3100P (12 L/min).",
      "Pair high flow with a wide surface cleaner — the combination of pressure, flow and coverage is what cleans driveways quickly.",
    ],
    faqs: [
      { q: "What's a good flow rate for a pressure washer?", a: "Domestic 7–9 L/min, prosumer 9–10 L/min, petrol/trade 10–15+ L/min." },
      { q: "Flow rate vs pressure — which matters more?", a: "For cleaning large areas — flow. For penetrating ingrained dirt — pressure. The best washers balance both." },
      { q: "Best high-flow pressure washer UK?", a: "Kärcher K7 Premium Smart Control for domestic; Hyundai HYW3100P petrol for trade and farm." },
    ],
    amazonQuery: "high flow pressure washer",
  },
  {
    slug: "high-pressure-pressure-washers",
    title: "High Pressure Washers",
    shortLabel: "High Pressure",
    group: "pressure",
    h1: `Best High Pressure Washers UK ${Y}`,
    metaTitle: `Best High Pressure Washers UK ${Y} | Top Power Picks`,
    metaDescription:
      "The best high pressure washers in the UK. Top-power petrol, electric and 3-phase models for trade, agricultural and demanding domestic cleaning.",
    intro:
      "High pressure washers — 170+ bar / 2,500+ PSI — handle every demanding job: large drives, rendered walls, oil stains, agricultural machinery.",
    longCopy: [
      "Top mains: Kärcher K7 Premium Smart Control (180 bar) and Kärcher HD 5/15 C (150 bar trade-grade). Top petrol: Hyundai HYW3100P (200+ bar). Top 3-phase: Kärcher HDS 9/20-4 M (200 bar hot-water).",
      "For sustained daily use, choose a triplex-pump trade model. For occasional weekend power, prosumer Kärcher K7 is excellent value.",
      "Always match pressure to surface — high pressure ruins soft stone, paint and bike bearings. Use the right nozzle for the job.",
    ],
    faqs: [
      { q: "What's considered high pressure in a washer?", a: "170+ bar / 2,500+ PSI. Below that is domestic; above is prosumer, trade or industrial." },
      { q: "Is high pressure always better?", a: "No — match it to the surface. Soft stone, paint, bearings and decals all suffer from too much pressure." },
      { q: "Best high pressure washer UK?", a: "Kärcher K7 Premium (mains domestic), Hyundai HYW3100P (petrol), Kärcher HD 5/15 C (trade single-phase)." },
    ],
    amazonQuery: "high pressure washer",
  },

  // ============ BY POWER SOURCE ============
  {
    slug: "mains-powered-pressure-washers",
    title: "Mains Powered Pressure Washers",
    shortLabel: "Mains 240V",
    group: "power",
    h1: `Best Mains Powered Pressure Washers UK ${Y}`,
    metaTitle: `Best Mains Powered Pressure Washers UK ${Y} | 240V Picks`,
    metaDescription:
      "The best mains powered pressure washers in the UK. 240V plug-in models from Kärcher, Nilfisk and Bosch reviewed for home and trade use.",
    intro:
      "Mains powered pressure washers are the default UK domestic choice — quiet, reliable and powerful enough for everything from cars to driveways.",
    longCopy: [
      "Every mains domestic washer runs from a standard 13A 240V outlet, drawing 1,400–2,500W. Trade single-phase models can pull 3,000W and need a dedicated 13A circuit.",
      "Top picks: Kärcher K4–K7 range, Nilfisk Core 125–150 Pro, Bosch EasyAquatak / UniversalAquatak / AdvancedAquatak ranges.",
      "Choose mains over petrol unless you need to wash off-grid. Mains is quieter, cleaner and cheaper to run.",
    ],
    faqs: [
      { q: "Can I plug a pressure washer into a normal socket?", a: "Yes — all UK domestic mains washers run from a standard 13A plug." },
      { q: "Mains vs petrol pressure washer?", a: "Mains is quieter, cheaper and easier. Petrol wins only when you're off-grid or need very high pressure." },
      { q: "Best mains pressure washer UK?", a: "Kärcher K5 Premium Smart Control for typical homes; Kärcher K7 for large gardens and rendered walls." },
    ],
    amazonQuery: "mains pressure washer",
  },
  {
    slug: "240v-pressure-washers",
    title: "240V Pressure Washers",
    shortLabel: "240V",
    group: "power",
    h1: `Best 240V Pressure Washers UK ${Y}`,
    metaTitle: `Best 240V Pressure Washers UK ${Y} | Plug-In Picks`,
    metaDescription:
      "Independent UK reviews of the best 240V pressure washers. Plug-in mains models from Kärcher, Nilfisk and Bosch reviewed for UK homes.",
    intro:
      "240V pressure washers are the standard UK domestic format — plug into any wall socket and clean cars, patios, driveways and decking.",
    longCopy: [
      "All 240V domestic washers run from a 13A plug. Power ranges from 1,400W (entry-level K2) to 2,500W (prosumer K7 / Nilfisk Excellent).",
      "Top 240V picks: Kärcher K2 to K7, Nilfisk Core and Excellent ranges, Bosch UniversalAquatak series. Pricing £80–£600.",
      "Higher-wattage 240V trade washers (e.g. Kärcher HD 5/15 C, 3,000W) need a dedicated 13A circuit.",
    ],
    faqs: [
      { q: "Are 240V pressure washers good?", a: "Yes — for 95% of UK households, a 240V mains model is the right choice. Quieter and cheaper to run than petrol." },
      { q: "Best 240V pressure washer UK?", a: "Kärcher K5 Premium Smart Control offers the best balance of power, features and reliability." },
      { q: "240V or petrol pressure washer?", a: "240V unless you need to wash off-grid or above 160 bar — then go petrol." },
    ],
    amazonQuery: "240v pressure washer",
  },
  {
    slug: "18v-pressure-washers",
    title: "18V Pressure Washers",
    shortLabel: "18V",
    group: "power",
    h1: `Best 18V Pressure Washers UK ${Y}`,
    metaTitle: `Best 18V Pressure Washers UK ${Y} | Cordless Reviews`,
    metaDescription:
      "The best 18V cordless pressure washers in the UK. Compare Makita LXT, DeWalt XR, Bosch and Worx battery models for bikes, cars and quick rinses.",
    intro:
      "18V pressure washers run on the same battery platform as your existing cordless drills — Makita LXT, DeWalt XR, Bosch 18V, Milwaukee M18.",
    longCopy: [
      "Single 18V cordless washers deliver 20–30 bar at 2–3 L/min — enough for bikes, dog washing, car detailing and quick rinses. Battery life is typically 20–30 minutes.",
      "Top 18V picks: Bosch Fontus 18V-30, Makita DHW080Z (twin-18V), Worx Hydroshot WG620E. Most are bare-tool — buy without battery if you're already in the ecosystem.",
      "For more pressure step up to 36V (dual-18V) or mains. 18V suits light cleaning and bike/car detailing best.",
    ],
    faqs: [
      { q: "Are 18V pressure washers any good?", a: "For light-duty cleaning — yes, especially if you're already in the brand's battery ecosystem. Don't expect mains-level pressure." },
      { q: "Best 18V pressure washer UK?", a: "Bosch Fontus 18V-30 for build quality; Worx Hydroshot for ecosystem flexibility (accepts most 18V batteries)." },
      { q: "How long does an 18V pressure washer run?", a: "20–35 minutes on a 4–5Ah battery. Spare batteries are essential for full-car washes." },
    ],
    amazonQuery: "18v pressure washer",
  },
  {
    slug: "40v-pressure-washers",
    title: "40V Pressure Washers",
    shortLabel: "40V / 36V",
    group: "power",
    h1: `Best 40V Pressure Washers UK ${Y}`,
    metaTitle: `Best 40V Pressure Washers UK ${Y} | High-Voltage Cordless`,
    metaDescription:
      "The best 40V and dual-18V (36V) cordless pressure washers in the UK. Higher-pressure battery picks from Greenworks, EGO, Karcher and Makita.",
    intro:
      "40V (and dual-18V 36V) cordless pressure washers deliver mains-class cleaning without a cable — strong enough for cars, decking and light patio work.",
    longCopy: [
      "These higher-voltage cordless platforms hit 40–60 bar at 4–6 L/min — twice the pressure of single-18V models. Runtime is typically 25–40 minutes on a 5Ah pack.",
      "Top picks: Greenworks 60V (60V badged but in the 40V power band), Karcher K3 Battery Set 36V, EGO Power+ HPW3000 (56V). Most include a battery and charger.",
      "Best for off-grid washing, allotments, campsites and car detailing where mains is inconvenient.",
    ],
    faqs: [
      { q: "Are 40V pressure washers better than 18V?", a: "Yes — roughly double the pressure and flow. Worth the extra cost if you do anything beyond bikes and rinsing." },
      { q: "Best 40V cordless pressure washer UK?", a: "Greenworks 60V or Karcher K3 Battery 36V — both deliver mains-class cleaning power cordlessly." },
      { q: "40V vs mains for patios?", a: "Mains is faster on large patios. 40V handles small patios and is perfect off-grid." },
    ],
    amazonQuery: "40v pressure washer",
  },

  // ============ BY BRAND ============
  {
    slug: "karcher-pressure-washers",
    title: "Kärcher Pressure Washers",
    shortLabel: "Kärcher",
    group: "brand",
    h1: `Best Kärcher Pressure Washers UK ${Y}`,
    metaTitle: `Best Kärcher Pressure Washers UK ${Y} | K2–K7 Reviews`,
    metaDescription:
      "Independent UK reviews of the best Kärcher pressure washers. K2, K3, K4, K5, K7 and Kärcher HD trade models compared.",
    intro:
      "Kärcher is the UK's best-selling pressure washer brand by a wide margin — the K-series (K2 to K7) covers entry-level to prosumer, and the HD range serves trade users.",
    longCopy: [
      "The Kärcher K-series ladder: K2 (110 bar entry), K3 (120 bar), K4 (130 bar all-rounder), K5 (145 bar best-seller), K7 (180 bar large-garden). Each step adds pressure, flow and accessories.",
      "Trade Kärcher: HD 5/15 C (cold-water single-phase), HDS 5/12 (hot-water), HD 9/20-4 M (3-phase). Built for daily 8-hour use with brass heads and ceramic pistons.",
      "Pair any Kärcher with their full accessory ecosystem — T7 patio cleaner, surface and gutter kits, snow-foam lance and replaceable hoses.",
    ],
    faqs: [
      { q: "Which Kärcher pressure washer is best?", a: "K5 Premium Smart Control for typical UK homes; K7 for large drives and rendered walls; HD 5/15 C for trade." },
      { q: "K4 vs K5 vs K7 — what's the difference?", a: "Mainly pressure and flow: K4 = 130 bar, K5 = 145 bar, K7 = 180 bar. K5/K7 have brass pump heads; K4 is plastic." },
      { q: "Are Kärcher pressure washers worth it?", a: "Yes — their accessory range, parts availability and dealer network make them the safest UK buy." },
    ],
    amazonQuery: "karcher pressure washer",
  },
  {
    slug: "stihl-pressure-washers",
    title: "STIHL Pressure Washers",
    shortLabel: "STIHL",
    group: "brand",
    h1: `Best STIHL Pressure Washers UK ${Y}`,
    metaTitle: `Best STIHL Pressure Washers UK ${Y} | RE Range Reviews`,
    metaDescription:
      "Independent UK reviews of STIHL pressure washers. The RE 90 to RE 282 PLUS range from dealer-only orchard, garden and trade users compared.",
    intro:
      "STIHL pressure washers — the RE-series — combine STIHL's dealer-network parts support with high-quality German engineering. Strongly favoured by gardeners and small-holders.",
    longCopy: [
      "Range overview: RE 90 (100 bar entry), RE 110 (115 bar), RE 130 PLUS (135 bar), RE 163 PLUS (150 bar), RE 232 PLUS (180 bar prosumer), RE 282 PLUS (200 bar trade).",
      "Hallmarks: brass pump heads on PLUS models, induction motors, robust steel frames and metal lances. Premium price reflects build quality.",
      "Best bought via your local STIHL dealer for trade-grade aftercare. Expect £200–£900 across the range.",
    ],
    faqs: [
      { q: "Are STIHL pressure washers worth the price?", a: "Yes for long-term build quality and dealer parts support. The PLUS models match or beat Karcher equivalents on durability." },
      { q: "STIHL RE 130 PLUS vs Kärcher K5?", a: "Comparable pressure and flow. STIHL has the dealer network; Kärcher has accessory range and lower price." },
      { q: "Where to buy STIHL pressure washers UK?", a: "STIHL is dealer-only — find a STIHL Approved Dealer rather than buying online." },
    ],
    amazonQuery: "stihl pressure washer",
  },
  {
    slug: "bosch-pressure-washers",
    title: "Bosch Pressure Washers",
    shortLabel: "Bosch",
    group: "brand",
    h1: `Best Bosch Pressure Washers UK ${Y}`,
    metaTitle: `Best Bosch Pressure Washers UK ${Y} | EasyAquatak to AdvancedAquatak`,
    metaDescription:
      "Independent UK reviews of Bosch pressure washers. EasyAquatak, UniversalAquatak, AdvancedAquatak and Fontus battery models compared.",
    intro:
      "Bosch pressure washers — the Aquatak series — deliver excellent build quality, integration with Bosch's 18V cordless ecosystem, and solid value for UK homes.",
    longCopy: [
      "Range: EasyAquatak 110 (entry), UniversalAquatak 125/135 (mid), AdvancedAquatak 140/150/160 (prosumer), Fontus 18V-30 (cordless).",
      "Hallmarks: integrated detergent tank, wheels and hose reel on mid and upper models, and Bosch's reputable 3-year warranty.",
      "Best for buyers already in the Bosch 18V cordless ecosystem — the Fontus battery washer uses your existing drill batteries.",
    ],
    faqs: [
      { q: "Are Bosch pressure washers good?", a: "Yes — they're consistently among the best-rated UK pressure washers for build and reliability." },
      { q: "Bosch AdvancedAquatak vs Kärcher K5?", a: "Comparable. Bosch has slightly more integrated features (detergent tank, hose reel). Kärcher has the larger accessory range." },
      { q: "Best Bosch pressure washer UK?", a: "AdvancedAquatak 160 for power; UniversalAquatak 135 for value; Fontus 18V-30 for cordless." },
    ],
    amazonQuery: "bosch pressure washer",
  },
  {
    slug: "nilfisk-pressure-washers",
    title: "Nilfisk Pressure Washers",
    shortLabel: "Nilfisk",
    group: "brand",
    h1: `Best Nilfisk Pressure Washers UK ${Y}`,
    metaTitle: `Best Nilfisk Pressure Washers UK ${Y} | Core, Excellent & MC`,
    metaDescription:
      "Independent UK reviews of Nilfisk pressure washers. Core, Excellent, Premium and MC trade ranges compared for home and commercial use.",
    intro:
      "Nilfisk is the Danish challenger to Kärcher — premium build, excellent reliability and a strong trade range (MC) that dominates UK contractor sales.",
    longCopy: [
      "Range: Core (110–150 Pro entry-mid), Excellent (160 Power prosumer), Premium (190 Power flagship). Trade: MC 2C, MC 4M, MC 5M cold-water; MH hot-water diesel-burner units.",
      "Hallmarks: brass pump heads on Pro/Excellent/Premium models, ceramic pistons on trade range, and Click&Clean accessory mounting.",
      "Strongly recommended for buyers who want a step up from Karcher in build but the same accessory convenience.",
    ],
    faqs: [
      { q: "Nilfisk or Kärcher — which is better?", a: "Nilfisk has marginally better build at the prosumer end (Core 140 Pro/Excellent 160). Kärcher has wider accessory range and slightly lower prices." },
      { q: "Best Nilfisk pressure washer UK?", a: "Core 140 Pro for value; Excellent 160 Power for prosumer; MC 2C for trade." },
      { q: "Are Nilfisk pressure washers worth it?", a: "Yes — especially the Pro and Excellent models which use brass pump heads at competitive prices." },
    ],
    amazonQuery: "nilfisk pressure washer",
  },
  {
    slug: "hyundai-pressure-washers",
    title: "Hyundai Pressure Washers",
    shortLabel: "Hyundai",
    group: "brand",
    h1: `Best Hyundai Pressure Washers UK ${Y}`,
    metaTitle: `Best Hyundai Pressure Washers UK ${Y} | Petrol & Electric`,
    metaDescription:
      "Independent UK reviews of Hyundai pressure washers. Petrol HYW and electric mains models reviewed for value, build and trade durability.",
    intro:
      "Hyundai Power Products has become the UK's best-value mid-tier brand — strong petrol and electric pressure washers that undercut Honda and Kärcher equivalents.",
    longCopy: [
      "Range: HYW3100P (petrol 220 bar), HYW2200P (entry petrol), Hyundai electric 2,000W and 2,200W models. All sold with a 3-year warranty.",
      "Petrol models are the headline — typically 30–50% cheaper than Honda-engine equivalents with similar specs. Build quality has improved markedly since 2022.",
      "Best for buyers who want petrol power without paying Honda money, or trade users who service in-house rather than relying on dealer networks.",
    ],
    faqs: [
      { q: "Are Hyundai pressure washers any good?", a: "Yes — modern Hyundai petrol washers are strong value with good 3-year warranty cover." },
      { q: "Hyundai HYW3100P vs Honda petrol?", a: "Honda has the edge on engine reliability; Hyundai wins decisively on price. For occasional weekend use, Hyundai is better value." },
      { q: "Best Hyundai pressure washer UK?", a: "HYW3100P petrol for off-grid power; 2,200W mains electric for value mid-range home use." },
    ],
    amazonQuery: "hyundai pressure washer",
  },
  {
    slug: "dewalt-pressure-washers",
    title: "DeWalt Pressure Washers",
    shortLabel: "DeWalt",
    group: "brand",
    h1: `Best DeWalt Pressure Washers UK ${Y}`,
    metaTitle: `Best DeWalt Pressure Washers UK ${Y} | Cordless & Petrol`,
    metaDescription:
      "Independent UK reviews of DeWalt pressure washers. DCPW550 cordless, DXPW petrol and DXPW electric models compared for trade users.",
    intro:
      "DeWalt's pressure washer range targets trade users — the DCPW550 cordless suits 18V XR battery owners, while the DXPW petrol and electric range covers serious site work.",
    longCopy: [
      "DCPW550 (18V cordless ~22 bar), DXPW3300E (petrol 3,300 PSI), DXPW3625 (4,200 PSI commercial), DXPW80E (electric 2,000 PSI).",
      "Hallmarks: brass pump heads on trade models, Honda or DeWalt-branded engines on petrol units, and robust steel roll cages.",
      "Best for tradespeople already in the DeWalt 18V XR ecosystem, or contractors wanting a single brand for power tools and washers.",
    ],
    faqs: [
      { q: "Are DeWalt pressure washers good?", a: "Yes — particularly the petrol DXPW range which offers commercial-grade pumps at competitive prices." },
      { q: "DeWalt DCPW550 vs Bosch Fontus?", a: "DCPW550 fits the XR battery ecosystem; Fontus is slightly higher pressure on Bosch 18V. Choose by tool ecosystem." },
      { q: "Best DeWalt pressure washer UK?", a: "DCPW550 for XR users; DXPW3300 for trade petrol." },
    ],
    amazonQuery: "dewalt pressure washer",
  },
  {
    slug: "makita-pressure-washers",
    title: "Makita Pressure Washers",
    shortLabel: "Makita",
    group: "brand",
    h1: `Best Makita Pressure Washers UK ${Y}`,
    metaTitle: `Best Makita Pressure Washers UK ${Y} | LXT & Mains Picks`,
    metaDescription:
      "Independent UK reviews of Makita pressure washers. DHW080 cordless twin-18V, HW132 mains and trade models compared.",
    intro:
      "Makita's pressure washer range pairs the LXT twin-18V cordless platform with capable mains electric models — strong for trade users already in Makita's ecosystem.",
    longCopy: [
      "DHW080 (twin-18V cordless ~80 bar), HW132 (mains 130 bar), HW140 (140 bar), HW1400 (140 bar workshop). Most ship with brass pump heads and induction motors.",
      "Hallmarks: tight build quality, Makita's UK dealer network, and integration with the wider LXT cordless platform.",
      "Best for trade users who own multiple Makita 18V tools — DHW080 uses two of your existing 18V batteries simultaneously.",
    ],
    faqs: [
      { q: "Are Makita pressure washers any good?", a: "Yes — solid build and pump quality, especially the twin-18V cordless DHW080 for trade users." },
      { q: "Makita DHW080 vs Bosch Fontus?", a: "DHW080 has higher pressure (twin-18V) but needs two LXT batteries. Fontus is smaller and runs on a single Bosch 18V battery." },
      { q: "Best Makita pressure washer UK?", a: "DHW080 cordless for LXT owners; HW132 mains for general workshop use." },
    ],
    amazonQuery: "makita pressure washer",
  },
  {
    slug: "sip-pressure-washers",
    title: "SIP Pressure Washers",
    shortLabel: "SIP",
    group: "brand",
    h1: `Best SIP Pressure Washers UK ${Y}`,
    metaTitle: `Best SIP Pressure Washers UK ${Y} | Tempest Reviews`,
    metaDescription:
      "Independent UK reviews of SIP pressure washers. Tempest electric, petrol and hot-water models for workshops, farms and contractors compared.",
    intro:
      "SIP is one of the UK's largest workshop equipment brands — the Tempest range covers domestic, agricultural and trade pressure washers at competitive prices.",
    longCopy: [
      "Range: Tempest PP540, TP570, TP750 (cold-water electric prosumer); TP680P-H (petrol); TPB740 (hot-water diesel-burner). Most use brass pump heads on the Pro models.",
      "SIP's dealer network and parts availability are well-regarded by UK trade users — easy to service and repair when something does need attention.",
      "Best for trade users wanting workshop-grade quality without paying Karcher / Nilfisk premium pricing.",
    ],
    faqs: [
      { q: "Are SIP pressure washers any good?", a: "Yes — particularly the Tempest prosumer and trade range, which offer brass pump heads at good prices." },
      { q: "SIP vs Nilfisk pressure washer?", a: "Nilfisk has slightly better build polish; SIP is cheaper and easier to service through UK dealers." },
      { q: "Best SIP pressure washer UK?", a: "Tempest TP750 cold-water for trade; TPB740 hot-water for serious workshop cleaning." },
    ],
    amazonQuery: "sip pressure washer",
  },
  {
    slug: "ava-pressure-washers",
    title: "AVA Pressure Washers",
    shortLabel: "AVA",
    group: "brand",
    h1: `Best AVA Pressure Washers UK ${Y}`,
    metaTitle: `Best AVA Pressure Washers UK ${Y} | Detailer-Favourite Picks`,
    metaDescription:
      "Independent UK reviews of AVA pressure washers. The Norwegian detailer-favourite range — Master, Smart, Evolution and Premium models compared.",
    intro:
      "AVA is the Norwegian brand UK car detailers swear by — modular, detailer-focused pressure washers with industry-leading snow-foam compatibility and build quality.",
    longCopy: [
      "Range: Smart P40 (entry), Master P55 (detailer favourite), Evolution P70, Premium P80. All use brass pump heads, induction motors and accept AVA's modular accessories.",
      "Hallmarks: very high build quality, premium pricing, modular accessory ecosystem, and a thriving detailer community for tips and aftermarket parts.",
      "Best for serious car detailers who want a long-life machine with industry-leading snow foam and chemistry compatibility.",
    ],
    faqs: [
      { q: "Are AVA pressure washers worth the money?", a: "Yes — for car detailers and serious enthusiasts. Build quality and longevity justify the premium." },
      { q: "AVA Master P55 vs Kärcher K5?", a: "AVA P55 is built better, lasts longer and has better snow-foam compatibility. K5 is cheaper and easier to service via Karcher dealers." },
      { q: "Best AVA pressure washer UK?", a: "Master P55 — the detailer favourite. Premium P80 for ultimate build, Smart P40 for entry-level AVA quality." },
    ],
    amazonQuery: "ava pressure washer",
  },
  {
    slug: "worx-pressure-washers",
    title: "Worx Pressure Washers",
    shortLabel: "Worx",
    group: "brand",
    h1: `Best Worx Pressure Washers UK ${Y}`,
    metaTitle: `Best Worx Pressure Washers UK ${Y} | Hydroshot Reviews`,
    metaDescription:
      "Independent UK reviews of Worx pressure washers. Hydroshot cordless and mains models from Worx reviewed for bikes, cars and quick rinsing.",
    intro:
      "Worx Hydroshot is the UK's most popular cordless 'pressure rinser' — a pistol-grip 20V battery washer that draws water from a bucket. Perfect for bikes, cars and quick clean-ups.",
    longCopy: [
      "Hydroshot range: WG620 (20V), WG629 (40V dual-battery). All draw water from a bucket, water butt or pond and weigh under 3 kg.",
      "Worx also makes corded mains washers (WG605, 120 bar) but the Hydroshot battery range is the standout product.",
      "Best for bike washing, dog washing, car detailing, beach gear, allotment use — anything where a full mains washer is overkill.",
    ],
    faqs: [
      { q: "Is the Worx Hydroshot any good?", a: "Yes — for light-duty cleaning and rinsing. Don't expect to clean a patio in any reasonable time." },
      { q: "Worx Hydroshot 20V vs 40V?", a: "40V dual-battery model has double the pressure and runtime. Strongly worth the extra cost." },
      { q: "Best Worx pressure washer UK?", a: "Hydroshot WG629 40V for cordless flexibility; WG605 for entry mains cleaning." },
    ],
    amazonQuery: "worx pressure washer",
  },

  // ============ BY USER TYPE ============
  {
    slug: "pressure-washers-for-homeowners",
    title: "Pressure Washers for Homeowners",
    shortLabel: "Homeowners",
    group: "user",
    h1: `Best Pressure Washers for Homeowners UK ${Y}`,
    metaTitle: `Best Pressure Washers for Homeowners UK ${Y} | Picks`,
    metaDescription:
      "The best pressure washers for UK homeowners. Step-by-step picks for cars, patios, decking and driveways from Kärcher, Nilfisk and Bosch.",
    intro:
      "For typical UK homeowners, a mid-range 130–150 bar mains washer with surface-cleaner attachment covers every common cleaning job — patios, cars, decking, fences and driveways.",
    longCopy: [
      "Sweet-spot picks: Kärcher K5 Premium Smart Control, Nilfisk Core 140 Pro, Bosch UniversalAquatak 135. Expect £200–£350 and 8–10 years of reliable weekend use.",
      "Add a surface cleaner for patios and a snow-foam lance for cars — these two accessories transform the usefulness of any domestic washer.",
      "Skip the cheapest sub-£100 models which rarely last 3 seasons. Skip the £600+ prosumer models unless you have a very large drive.",
    ],
    faqs: [
      { q: "What pressure washer should a homeowner buy?", a: "130–150 bar mains electric with brass pump head and 3-year warranty. Kärcher K5 or Nilfisk Core 140 Pro." },
      { q: "How much should a homeowner spend on a pressure washer?", a: "£200–£350 for an 8-10 year machine. Below £150 you compromise on longevity." },
      { q: "Best all-round pressure washer for a UK home?", a: "Kärcher K5 Premium Smart Control — does everything most households need." },
    ],
    amazonQuery: "pressure washer for homeowners",
  },
  {
    slug: "pressure-washers-for-gardeners",
    title: "Pressure Washers for Gardeners",
    shortLabel: "Gardeners",
    group: "user",
    h1: `Best Pressure Washers for Gardeners UK ${Y}`,
    metaTitle: `Best Pressure Washers for Gardeners UK ${Y} | Picks`,
    metaDescription:
      "The best pressure washers for professional and amateur gardeners in the UK. Portable petrol and electric picks for jobbing gardeners and allotment users.",
    intro:
      "Jobbing gardeners need a pressure washer that travels between sites and tackles patios, decking, paths and garden furniture with minimum fuss.",
    longCopy: [
      "For mobile gardeners, a wheeled mains unit (Kärcher K5 Premium or Nilfisk Core 140 Pro) plus a long extension cable covers most jobs. For larger sites without convenient mains, step up to a Hyundai HYW3100P petrol.",
      "Always carry a surface-cleaner attachment — they transform productivity on patios and small drives.",
      "For allotment work where mains is rare, a battery-powered Bosch Fontus or Worx Hydroshot 40V is invaluable.",
    ],
    faqs: [
      { q: "Best pressure washer for jobbing gardener?", a: "Kärcher K5 Premium for mains sites; Hyundai HYW3100P petrol for off-grid; Bosch Fontus battery for allotments." },
      { q: "Do gardeners need a petrol pressure washer?", a: "Only if you regularly work without mains. For most home gardens mains is faster, quieter and cheaper to run." },
      { q: "How much should a gardener spend on a pressure washer?", a: "£250–£600 for trade-grade reliability. Cheap models won't survive daily site use." },
    ],
    amazonQuery: "pressure washer for gardeners",
  },
  {
    slug: "pressure-washers-for-car-detailers",
    title: "Pressure Washers for Car Detailers",
    shortLabel: "Detailers",
    group: "user",
    h1: `Best Pressure Washers for Car Detailers UK ${Y}`,
    metaTitle: `Best Pressure Washers for Car Detailers UK ${Y} | Picks`,
    metaDescription:
      "The best pressure washers for UK car detailers. Snow-foam-friendly picks from AVA, Karcher and Nilfisk with detailer-grade pump quality.",
    intro:
      "Car detailers prioritise snow-foam compatibility, paint-safe pressure, long pump life and easy serviceability — a different shopping list to general homeowners.",
    longCopy: [
      "Top detailer picks: AVA Master P55 (community favourite), Kärcher K5 Premium (best value with MTM PF22.2 snow lance), Nilfisk Core 140 Pro.",
      "Pair with a quality snow-foam lance (MTM PF22.2 with Karcher quick-connect) and a pH-neutral foam (Auto Finesse Avalanche, Bilberry).",
      "Avoid the cheapest models — detailers run their washers far more hours than typical homeowners and value pump longevity highly.",
    ],
    faqs: [
      { q: "What pressure washer do car detailers use?", a: "AVA Master P55, Kärcher K5/K7 Premium, Nilfisk Core 140/150 Pro — all paired with an MTM-style snow-foam lance." },
      { q: "Best pressure washer for snow foam?", a: "AVA Master P55 with the AVA snow lance, or any Karcher K-series with an MTM PF22.2 (Karcher quick-connect) snow lance." },
      { q: "Is the K5 good for detailing?", a: "Yes — combined with a quality snow-foam lance, the K5 is the UK's most popular detailer washer." },
    ],
    amazonQuery: "pressure washer for car detailing",
  },
  {
    slug: "pressure-washers-for-farmers",
    title: "Pressure Washers for Farmers",
    shortLabel: "Farmers",
    group: "user",
    h1: `Best Pressure Washers for Farmers UK ${Y}`,
    metaTitle: `Best Pressure Washers for Farmers UK ${Y} | Top Picks`,
    metaDescription:
      "The best pressure washers for UK farmers and smallholders. Petrol, diesel and hot-water industrial picks for tractors, yards and livestock cleaning.",
    intro:
      "Farms need robust, mobile, often hot-water pressure washers for tractors, machinery, yards, milking parlours and animal housing.",
    longCopy: [
      "For cold-water mobile cleaning, Hyundai HYW3100P petrol or Honda-powered Kärcher G-series. For grease, silage and parlour deep-cleans, Kärcher HDS hot-water trade or trailer-mounted units.",
      "Three-phase 3-bar+ workshop washers (Kärcher HD 9/20-4 M, Nilfisk MC 5M) suit fixed milking-parlour installations.",
      "Don't skimp on pump quality — farm washers run hard and any time saved on cleaning pays back faster than on any other site.",
    ],
    faqs: [
      { q: "Best pressure washer for a farm?", a: "Cold cleaning: Hyundai HYW3100P petrol. Hot cleaning: Kärcher HDS 5/12. Mobile contract: trailer-mounted HDS Trailer." },
      { q: "Do I need hot water on a farm pressure washer?", a: "For dairy parlours and grease/oil cleaning — yes, hot water is a major time-saver. For mud and chaff, cold is fine." },
      { q: "Cold or diesel pressure washer for tractors?", a: "Cold high-pressure petrol for routine mud; hot-water diesel-burner for grease and silage residue." },
    ],
    amazonQuery: "pressure washer for farm",
  },
  {
    slug: "pressure-washers-for-tradespeople",
    title: "Pressure Washers for Tradespeople",
    shortLabel: "Tradespeople",
    group: "user",
    h1: `Best Pressure Washers for Tradespeople UK ${Y}`,
    metaTitle: `Best Pressure Washers for Tradespeople UK ${Y} | Picks`,
    metaDescription:
      "The best pressure washers for UK tradespeople. Builders, decorators, valeters and contractors compared with mains, petrol and cordless picks.",
    intro:
      "Tradespeople need reliable, mobile pressure washers — different roles need different tools. The right pick saves time and outlasts five cheap units.",
    longCopy: [
      "Builders and roofers: Hyundai HYW3100P petrol or Karcher HD 5/15 C single-phase. Painters and decorators: Karcher K5 plus extension lance for render and woodwork. Valeters: AVA P55 or K7 with snow-foam lance. Cleaners: Karcher HDS hot-water trade.",
      "Always choose triplex-pump models for daily use. Axial-pump prosumer washers fail under sustained trade hours.",
      "Mobile tradespeople should look at trailer hot-water units for full-service mobile cleaning, or invest in a quality petrol mobile washer.",
    ],
    faqs: [
      { q: "Best pressure washer for a builder?", a: "Karcher HD 5/15 C (single-phase) or Hyundai HYW3100P (petrol). Both have trade-grade triplex pumps." },
      { q: "Cordless pressure washer for trade?", a: "DeWalt DCPW550 (18V XR), Makita DHW080 (twin-18V), Milwaukee M18 — choose by tool ecosystem." },
      { q: "Best pressure washer for valeting?", a: "AVA Master P55 or Karcher K7 Premium with MTM snow-foam lance." },
    ],
    amazonQuery: "pressure washer for tradespeople",
  },
  {
    slug: "pressure-washers-for-professionals",
    title: "Pressure Washers for Professionals",
    shortLabel: "Professionals",
    group: "user",
    h1: `Best Pressure Washers for Professionals UK ${Y}`,
    metaTitle: `Best Pressure Washers for Professionals UK ${Y} | Top Picks`,
    metaDescription:
      "Top pressure washers for UK professionals. Trade-grade cold and hot-water picks from Kärcher, Nilfisk, Comet and SIP for daily commercial use.",
    intro:
      "Professionals washing for a living need brass pump heads, ceramic pistons, induction motors and triplex pumps — features absent from sub-£500 domestic models.",
    longCopy: [
      "Cold-water pro: Kärcher HD 5/15 C (single-phase), Nilfisk MC 2C, Comet KS 1900, SIP Tempest TP750. Hot-water pro: Kärcher HDS 5/12, Nilfisk MH 3M-100/600, Lavor Thermic 17H.",
      "All run for 8,000–15,000+ hours with regular seal kit replacements. Total cost of ownership is far lower than burning through DIY units annually.",
      "Pair with quality lances, surface cleaners and dosing kits for chemical-assisted cleaning — chemistry plus pressure beats pressure alone.",
    ],
    faqs: [
      { q: "What makes a pressure washer professional?", a: "Triplex pump, brass head, ceramic pistons, induction motor and serviceable seals." },
      { q: "How much does a pro pressure washer cost?", a: "£700–£2,500 for cold-water single-phase; £2,500–£8,000+ for hot-water diesel-burner." },
      { q: "Best professional pressure washer UK?", a: "Kärcher HD 5/15 C is the UK's best-selling pro single-phase washer. Nilfisk MC 2C is the closest competitor." },
    ],
    amazonQuery: "professional pressure washer",
  },
  {
    slug: "pressure-washers-for-diy",
    title: "Pressure Washers for DIY",
    shortLabel: "DIY",
    group: "user",
    h1: `Best Pressure Washers for DIY UK ${Y}`,
    metaTitle: `Best Pressure Washers for DIY UK ${Y} | Top Picks`,
    metaDescription:
      "The best pressure washers for UK DIYers. Easy-to-use mains and battery picks for occasional patios, decking, fencing and car-cleaning projects.",
    intro:
      "For DIY weekend use, a 120–140 bar mains washer handles every common project — fence cleaning before staining, patio scrubbing, car washing and tool cleaning.",
    longCopy: [
      "Best DIY picks: Kärcher K4 Power Control, Nilfisk Core 125-5, Bosch UniversalAquatak 135. Expect £150–£250 and 5–8 years of weekend service.",
      "Add a surface-cleaner attachment for patios and a fence/wood mode (Karcher Power Control) for paint-prep work.",
      "Don't overbuy — a K7 is overkill for occasional DIY. Save the money for a quality surface cleaner and a few decent accessories.",
    ],
    faqs: [
      { q: "Best DIY pressure washer UK?", a: "Kärcher K4 Power Control — perfect blend of pressure, accessories and price for occasional weekend use." },
      { q: "How much should I spend on a DIY pressure washer?", a: "£150–£250. Below £100 you compromise on pump life; above £400 is unnecessary for occasional use." },
      { q: "Mains or battery for DIY?", a: "Mains for value and power; battery only if you regularly clean off-grid (allotment, motorhome, etc.)." },
    ],
    amazonQuery: "pressure washer for diy",
  },
  {
    slug: "pressure-washers-for-beginners",
    title: "Pressure Washers for Beginners",
    shortLabel: "Beginners",
    group: "user",
    h1: `Best Pressure Washers for Beginners UK ${Y}`,
    metaTitle: `Best Pressure Washers for Beginners UK ${Y} | Easy Picks`,
    metaDescription:
      "The best pressure washers for UK beginners. Easy-to-use, safe entry-level mains models with clear instructions and forgiving controls.",
    intro:
      "First-time buyers should prioritise simplicity, safety and good documentation over headline pressure figures.",
    longCopy: [
      "Top beginner picks: Kärcher K2 Premium Full Control, Nilfisk Core 110, Bosch EasyAquatak 110. All are around £80–£150 and come with clear video setup guides.",
      "Features to look for: total-stop trigger, integrated detergent tank, quick-connect tap fitting, and an included patio-cleaner attachment.",
      "Avoid turbo nozzles until you understand pressure — they look exciting but can damage paint, soft stone and decking quickly.",
    ],
    faqs: [
      { q: "Best pressure washer for a first-time buyer?", a: "Kärcher K2 Premium Full Control — easy setup, total-stop trigger, integrated detergent tank." },
      { q: "How much should a beginner spend?", a: "£80–£200 covers a quality entry-level washer with 2–3 year warranty." },
      { q: "What pressure for a beginner?", a: "Stick to 100–130 bar — easy to control, less risk of surface damage while you learn." },
    ],
    amazonQuery: "pressure washer for beginners",
  },

  // ============ BY NOISE / CONVENIENCE ============
  {
    slug: "quiet-pressure-washers",
    title: "Quiet Pressure Washers",
    shortLabel: "Quiet",
    group: "noise",
    h1: `Best Quiet Pressure Washers UK ${Y}`,
    metaTitle: `Best Quiet Pressure Washers UK ${Y} | Low-Noise Picks`,
    metaDescription:
      "The quietest pressure washers in the UK. Induction-motor electric models under 75 dB for terraced houses, courtyards and noise-sensitive areas.",
    intro:
      "If you live in a terrace, courtyard or noise-sensitive area, an induction-motor electric pressure washer is dramatically quieter than petrol or universal-motor alternatives.",
    longCopy: [
      "Quietest models run at 70–78 dB at 1 m — equivalent to a normal conversation. Look for induction motors (Karcher K4/K5/K7 Premium, Nilfisk Core Pro, Bosch AdvancedAquatak).",
      "Avoid universal-motor cheap models which run 90–95 dB. Avoid petrol entirely if noise matters — most petrol washers are 95–105 dB.",
      "Cordless 18V/40V models are also relatively quiet but trade off pressure for portability.",
    ],
    faqs: [
      { q: "What's the quietest pressure washer?", a: "Induction-motor electric models like the Kärcher K5/K7 Premium or Nilfisk Core 140 Pro — typically 70–75 dB." },
      { q: "How loud is a pressure washer?", a: "Cheap universal-motor: 90–95 dB. Quality induction: 70–78 dB. Petrol: 95–105 dB. Cordless battery: 75–82 dB." },
      { q: "Are induction-motor pressure washers worth the extra money?", a: "Yes if noise matters or you use it weekly — they're quieter and last 2–3× longer than universal-motor models." },
    ],
    amazonQuery: "quiet pressure washer",
  },
  {
    slug: "lightweight-pressure-washers",
    title: "Lightweight Pressure Washers",
    shortLabel: "Lightweight",
    group: "noise",
    h1: `Best Lightweight Pressure Washers UK ${Y}`,
    metaTitle: `Best Lightweight Pressure Washers UK ${Y} | Easy-to-Carry`,
    metaDescription:
      "The best lightweight pressure washers in the UK — under 8 kg. Easy-carry mains and battery models for older users, flats and quick deployment.",
    intro:
      "Lightweight pressure washers (under 8 kg) suit older users, flats with stairs, mobile workers and anyone who values quick deploy-and-go over wheeled trolley designs.",
    longCopy: [
      "Best lightweight mains: Kärcher K2 Premium (5 kg), Bosch EasyAquatak 110 (5 kg), Nilfisk Core 110 (6 kg). Best lightweight cordless: Worx Hydroshot WG620 (2 kg), Bosch Fontus (3 kg).",
      "Trade-off: lightweight usually means lower pressure (100–125 bar). For driveways or rendered walls, accept a heavier 8–12 kg mid-size unit.",
      "Look for a carry handle, integrated hose storage and a removable detergent tank.",
    ],
    faqs: [
      { q: "What's the lightest pressure washer?", a: "Worx Hydroshot at 2 kg (cordless); Kärcher K2 Premium at 5 kg (mains)." },
      { q: "Are lightweight pressure washers underpowered?", a: "Usually 100–125 bar — plenty for cars, bikes and small patios; underpowered for full driveways." },
      { q: "Best lightweight pressure washer for older users?", a: "Kärcher K2 Premium Full Control with full hose storage — easy to lift and operate." },
    ],
    amazonQuery: "lightweight pressure washer",
  },
  {
    slug: "easy-storage-pressure-washers",
    title: "Easy Storage Pressure Washers",
    shortLabel: "Easy Storage",
    group: "noise",
    h1: `Best Easy Storage Pressure Washers UK ${Y}`,
    metaTitle: `Best Easy Storage Pressure Washers UK ${Y} | Compact Picks`,
    metaDescription:
      "The best pressure washers for easy storage in the UK. Integrated hose reels, lance holders and compact uprights for small sheds and garages.",
    intro:
      "Easy-storage pressure washers have integrated hose reels, lance holders, accessory clips and compact uprights — saving real shed-shelf space.",
    longCopy: [
      "Look for full hose-reel models (Karcher K5/K7 Premium, Nilfisk Core 140 Pro Compact, Bosch AdvancedAquatak) which coil the hose neatly with no tangles.",
      "Lance holders, nozzle clips and accessory bays on the unit itself mean everything stays together between uses.",
      "For tiny spaces, the Kärcher K2 Compact stands under 30 cm tall and fits on a kitchen cupboard shelf.",
    ],
    faqs: [
      { q: "Best pressure washer with a hose reel?", a: "Kärcher K5 Premium Smart Control — tidy reel, lance storage, accessory clips and 8m hose all in one tower." },
      { q: "How do I store a pressure washer?", a: "Drain water from pump and hose, coil neatly, store upright indoors. Frost protection is essential in winter." },
      { q: "Best compact pressure washer for shed storage?", a: "Kärcher K2 Compact at under 30 cm tall fits virtually anywhere." },
    ],
    amazonQuery: "pressure washer with hose reel",
  },
  {
    slug: "low-noise-pressure-washers",
    title: "Low Noise Pressure Washers",
    shortLabel: "Low Noise",
    group: "noise",
    h1: `Best Low Noise Pressure Washers UK ${Y}`,
    metaTitle: `Best Low Noise Pressure Washers UK ${Y} | Reviews`,
    metaDescription:
      "The lowest-noise pressure washers in the UK. Induction-motor and battery models reviewed for quiet operation in residential gardens.",
    intro:
      "Low noise pressure washers — induction-motor mains and cordless battery models — are essential for urban use, terraced gardens and residential areas with noise restrictions.",
    longCopy: [
      "Top low-noise picks: Kärcher K5/K7 Premium Smart Control (induction motor, 75 dB), Nilfisk Core 140 Pro Compact (74 dB), Bosch UniversalAquatak 135 (74 dB).",
      "Avoid universal-motor budget models — they're audibly louder and have shorter pump life.",
      "Battery washers (Worx Hydroshot, Bosch Fontus) are quieter still and ideal for shared courtyards or early-morning use.",
    ],
    faqs: [
      { q: "What's a low-noise pressure washer?", a: "Anything under 78 dB at 1 m — usually means an induction-motor electric or cordless battery model." },
      { q: "Are battery pressure washers quieter than mains?", a: "Slightly, yes — cordless models run at 75–80 dB versus 75–95 dB for mains." },
      { q: "Best quiet pressure washer for urban use?", a: "Kärcher K5 Premium Smart Control or Worx Hydroshot 40V — both under 78 dB." },
    ],
    amazonQuery: "low noise pressure washer",
  },
];

// Auto-derive tag keywords (lower-cased) for each subcategory.
export const pressureWasherSubcategories: Record<string, PressureWasherSubcategoryConfig> =
  Object.fromEntries(
    seeds.map(seed => {
      const baseTags = Array.from(
        new Set(
          [
            seed.title,
            seed.title.toLowerCase(),
            seed.shortLabel,
            seed.shortLabel.toLowerCase(),
            seed.slug,
            seed.slug.replace(/-/g, " "),
            "pressure washer",
            "pressure washers",
            "jet wash",
            seed.amazonQuery,
          ]
            .filter(Boolean)
            .map(s => s.toLowerCase())
        )
      );
      return [seed.slug, { ...seed, tagKeywords: seed.tagKeywords ?? baseTags }];
    })
  );

export const pressureWasherSubcategoryList = Object.values(pressureWasherSubcategories);

export const pressureWasherGroupLabels: Record<PressureWasherGroup, string> = {
  type: "By Type",
  use: "By Use Case",
  pressure: "By Pressure",
  power: "By Power Source",
  brand: "By Brand",
  user: "By User Type",
  noise: "By Noise / Convenience",
};

export const pressureWasherGroupOrder: PressureWasherGroup[] = [
  "type",
  "use",
  "pressure",
  "power",
  "brand",
  "user",
  "noise",
];
